import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toast } from "primereact/toast";
import FetchMvrModal from "./FetchMvrModal";
import OrderMvrModal from "./OrderMvrModal";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
type DocStatus = "checked" | "missing" | "uploaded";

interface DocItem {
  id: string;
  label: string;
  description: string;
  file: File | null;
  status: DocStatus;
  dragOver: boolean;
  restoredName?: string;
  restoredSize?: number;
  restoredUrl?: string;
}

interface MvrFile {
  file: File | null;
  fileId?: string;
  restoredName?: string;
  restoredSize?: number;
  restoredUrl?: string;
  loading: boolean;
  error: string | null;
  result: AnalysisResult | null;
  viewLoading: boolean;
  viewResult: any | null;
  viewOpen: boolean;
}

interface DriverMvrState {
  driverName: string;
  licenseNumber: string;
  state: string;
  files: MvrFile[];
  dragOver: boolean;
}

interface DocViewState {
  loading: boolean;
  result: any | null;
  open: boolean;
}

interface SubmissionSummaryResult {
  quoteId: string;
  status: string;
  documents: Record<string, string>;
  driverMvrStatus: any[];
  qualityScore: number;
  summary: string;
  report: any;
  reportMarkdown?: string;
  canSubmit: boolean;
}

interface RelatedCarrierSharedVin {
  vin?: string;
  unitType?: string;
  lastInspection?: string;
}

/** Another trucking company sharing an identifier with the insured — the same
 *  thing a CAB report's Chameleon Carrier page names. Insurance carriers ask us
 *  to explain these before they will quote, so we raise them pre-submission. */
interface RelatedCarrier {
  dotNumber: string;
  legalName?: string | null;
  usdotStatus?: string | null;
  primaryContact?: string | null;
  /** CAB's column names: Address, Phone, E-Mail, Rep, Vehicle, EIN, DUNS, Fax. */
  matchedOn?: string[];
  /** 1 = stated by the source; 2+ = derived by walking the relationship graph. */
  depth?: number;
  /** DOT chain from the insured out to this carrier, inclusive of both. */
  path?: string[];
  sharedVins?: RelatedCarrierSharedVin[];
  source?: string;
}

interface AnalysisResult {
  success: boolean;
  fileURL: string;
  data: {
    quoteId: string;
    documentType: string;
    status: string;
    summary: string;
    ready: boolean;
  };
}

interface AmsNote {
  databaseId: string;
  subject: string | null;
  noteType: string | null;
  createdBy: string | null;
  createDate: string | null;
  changeDate: string | null;
  insuredDatabaseId: string | null;
  policyDatabaseId: string | null;
  policy: { number: string | null; carrierName: string | null; linesOfBusiness: string[] | null } | null;
}

/* ─────────────────────────────────────────────
   Constants & helpers
───────────────────────────────────────────── */
const stripHtml = (html: string | null): string => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
};
const ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const formatBytes = (bytes: number | undefined | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const INITIAL_DOCS: DocItem[] = [
  {
    id: "safers",
    label: "SAFERS Status",
    description: "Motor carrier safety rating from FMCSA SAFER system",
    file: null,
    status: "missing",
    dragOver: false,
  },
  {
    id: "cab",
    label: "CAB Report Status",
    description: "Carrier Assessment and Benchmarking report",
    file: null,
    status: "missing",
    dragOver: false,
  },
];

const INIT_VIEW: DocViewState = { loading: false, result: null, open: false };

/* ── CAB / SAFER underwriting checks ── */

/** An alert raised on the FMCSA SAFER snapshot's checkbox lists. */
interface SafersAlert {
  code: string;
  label: string;
  severity?: string;
  message?: string;
  values?: string[];
}

/** These fields arrive at different depths depending on the endpoint: top-level on
 *  the AI API's submission responses, nested under `analysis` on a restored record.
 *  The .NET document endpoints are deliberately not a source — their typed DTOs
 *  drop the fields entirely. */
const PAYLOAD_ROOTS = (payload: any) => [payload, payload?.data, payload?.analysis, payload?.data?.analysis];

/** Whether the payload speaks to a subject at all. Lets an authoritative empty
 *  result clear a warning, while a response that never carried the fields is
 *  ignored rather than treated as "nothing found". */
const carriesAnyKey = (payload: any, keys: string[]): boolean =>
  PAYLOAD_ROOTS(payload).some((root) => root != null && keys.some((k) => k in root));

const extractSafersAlerts = (payload: any): SafersAlert[] => {
  for (const root of PAYLOAD_ROOTS(payload)) {
    const list = root?.safersAlerts ?? root?.alerts;
    if (Array.isArray(list) && list.length) return list.filter((a: any) => a?.code);
  }
  return [];
};

const carriesSafersAlertFields = (payload: any): boolean =>
  carriesAnyKey(payload, ["hasSafersAlerts", "safersAlerts", "alerts"]);

const extractRelatedCarriers = (payload: any): RelatedCarrier[] => {
  for (const root of PAYLOAD_ROOTS(payload)) {
    const list = root?.relatedCarriers;
    if (Array.isArray(list) && list.length) return list.filter((c: any) => c?.dotNumber);
  }
  return [];
};

/** The flag can be true with an empty list — the indicator is free but naming the
 *  counterparties can time out — so the warning must still show. */
const hasRelationshipFlag = (payload: any): boolean =>
  PAYLOAD_ROOTS(payload).some(
    (root) => root?.hasRelatedCarriers || root?.relationshipSummary?.hasRelationship,
  );

const carriesRelationshipFields = (payload: any): boolean =>
  carriesAnyKey(payload, ["hasRelatedCarriers", "relatedCarriers", "relationshipSummary"]);

const MATCH_ICONS: Record<string, string> = {
  "Address": "⌂", "Phone": "✆", "E-Mail": "✉", "Rep": "☻",
  "Vehicle": "⛟", "EIN": "#", "DUNS": "#", "Fax": "✇",
};

/** A shared officer, e-mail, phone or tax ID implies common control. A shared
 *  vehicle is routinely a lease or resale, so it escalates less. */
const STRONG_MATCHES = new Set(["Rep", "E-Mail", "EIN", "DUNS", "Phone"]);

const isStrongMatch = (c: RelatedCarrier): boolean =>
  (c.matchedOn ?? []).some((m) => STRONG_MATCHES.has(m));

const val = (v?: string | null) => (v && v !== "null" && v.trim() !== "" ? v : "—");

const fmtCurrency = (v?: string | number) => {
  if (v === "" || v === null || v === undefined) return "—";
  const num = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(num);
};

const statusClass = (s?: string) =>
  ({
    Approved: "badge badge--hero badge--hero-approved",
    Pending: "badge badge--hero badge--hero-pending",
    Rejected: "badge badge--hero badge--hero-rejected",
  })[s ?? ""] ?? "badge badge--hero badge--hero-pending";

/* ── Verdict status badge helper ── */
const _VERDICT_STATUS_MAP: Record<string, string> = {
  'VIOLATED': 'violated',
  'PARTIALLY MET': 'partial',
  'MET': 'met',
  'INCONCLUSIVE': 'inconclusive',
  'APPLIES': 'partial',
  'N/A': 'na',
};

function injectStatusBadges(text: string): React.ReactNode {
  const parts = text.split(/(VIOLATED|PARTIALLY MET|MET|INCONCLUSIVE|APPLIES|N\/A)/);
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        _VERDICT_STATUS_MAP[part]
          ? <span key={i} className={`sv-status sv-status--${_VERDICT_STATUS_MAP[part]}`}>{part}</span>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

function processVerdictChildren(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, child =>
    typeof child === 'string' ? injectStatusBadges(child) : child
  );
}

const verdictMdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  h1: ({ children }) => <div className="sv-section-header">{children}</div>,
  h2: ({ children }) => <div className="sv-section-header">{children}</div>,
  h3: ({ children }) => <div className="sv-section-header">{children}</div>,
  p: ({ children, node }: any) => {
    if (node?.children?.length === 1 && node.children[0]?.type === 'strong') {
      return <div className="sv-section-header">{children}</div>;
    }
    return <p className="sv-p">{processVerdictChildren(children)}</p>;
  },
  strong: ({ children }) => <span className="sv-strong">{children}</span>,
  ul: ({ children }) => <ul className="sv-list">{children}</ul>,
  ol: ({ children }) => <ol className="sv-list">{children}</ol>,
  li: ({ children }) => <li className="sv-item">{processVerdictChildren(children)}</li>,
  table: ({ children }) => <div className="sv-table-wrap"><table className="sv-table">{children}</table></div>,
  thead: ({ children }) => <thead className="sv-thead">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="sv-tr">{children}</tr>,
  th: ({ children }) => <th className="sv-th">{children}</th>,
  td: ({ children }) => <td className="sv-td">{processVerdictChildren(children)}</td>,
};

/* ── Styled Markdown renderer ── */
const mdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  table: ({ children }) => (
    <div className="dr-md-table-wrap">
      <table className="dr-md-table">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="dr-md-thead">{children}</thead>,
  th: ({ children }) => <th className="dr-md-th">{children}</th>,
  td: ({ children }) => <td className="dr-md-td">{children}</td>,
  tr: ({ children, ...props }) => <tr className="dr-md-tr" {...props}>{children}</tr>,
  h1: ({ children }) => <p className="dr-md-h1">{children}</p>,
  h2: ({ children }) => <p className="dr-md-h2">{children}</p>,
  h3: ({ children }) => <p className="dr-md-h3">{children}</p>,
  p: ({ children }) => <p className="dr-md-p">{children}</p>,
  strong: ({ children }) => <strong className="dr-md-strong">{children}</strong>,
  em: ({ children }) => <em className="dr-md-em">{children}</em>,
};

/* ── Shared result panel ── */
const AnalysisPanel: React.FC<{ data: any }> = ({ data }) => {
  const d = data?.data ?? data;
  return (
    <div className="ap-panel">
      {d?.documentType && (
        <div className="ap-doc-row">
          <span className="ap-doc-type-key">Document</span>
          <span className="ap-doc-type-val">{String(d.documentType).replaceAll("_", " ")}</span>
          {d?.status && (
            <span className={`ap-status-badge ap-status-badge--${d.status === "processed" ? "processed" : "pending"}`}>
              {String(d.status).toUpperCase()}
            </span>
          )}
        </div>
      )}
      {d?.summary && (
        <div>
          <div className="ap-summary-label">Summary</div>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {d.summary}
          </ReactMarkdown>
        </div>
      )}
      {d?.ready !== undefined && (
        <div className="ap-ready-row">
          <span className="ap-ready-key">Ready</span>
          <span className={`ap-ready-badge ap-ready-badge--${d.ready ? "yes" : "no"}`}>
            {d.ready ? "Yes" : "No"}
          </span>
        </div>
      )}
    </div>
  );
};

/* ── Related Carriers card ──
   Sits under the Carrier Verdict in the Submission Summary: this is the question
   an insurance carrier would otherwise email us after we submit, so it belongs
   where the underwriter reads the verdict. */
const SOURCE_LABELS: Record<string, string> = {
  cab_report: "CAB report, Chameleon Carrier page",
  carrierok_network_graph: "FMCSA carrier records (no CAB report needed)",
};

const RelatedCarriersCard: React.FC<{ carriers: RelatedCarrier[]; flagged: boolean; cabChecked: boolean }> = ({ carriers, flagged, cabChecked }) => {
  // Nothing found, but CAB was analysed: say so explicitly. Silence reads as
  // "not checked", and the underwriter needs the negative on record too.
  if (!flagged && !carriers.length) {
    if (!cabChecked) return null;
    return (
      <div className="ssm-card ssm-card--related-clear">
        <div className="ssm-section-label ssm-section-label--ok">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Related Carriers
        </div>
        <p className="ssm-related-intro">
          No related carriers found. This carrier shares no address, phone, e-mail, officer,
          or inspected vehicle with another trucking company on record.
        </p>
      </div>
    );
  }

  const severity = carriers.some(isStrongMatch) ? "high" : "warn";
  const sources = Array.from(new Set(carriers.map((c) => c.source).filter(Boolean) as string[]));

  return (
    <div className={`ssm-card ssm-card--related ssm-card--related-${severity}`}>
      <div className="ssm-section-label ssm-section-label--warn">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        Related Carriers
        <span className="ssm-related-count">{carriers.length || "flagged"}</span>
      </div>

      <p className="ssm-related-intro">
        {carriers.length > 0 ? (
          <>
            This carrier shares identifying details with{" "}
            <strong>{carriers.length} other {carriers.length === 1 ? "carrier" : "carriers"}</strong>.
            Insurance carriers review the same data and will ask you to explain the relationship —
            include an explanation with the submission to avoid the round trip.
          </>
        ) : (
          <>
            A relationship to another carrier was flagged, but the related carriers could not be
            listed. Re-run the CAB lookup, or check the CAB report's Chameleon Carrier page.
          </>
        )}
      </p>

      {carriers.length > 0 && (
        <div className="ssm-related-list">
          {carriers.map((c) => (
            <div key={c.dotNumber} className={`ssm-related-row${isStrongMatch(c) ? " ssm-related-row--strong" : ""}`}>
              <div className="ssm-related-row__head">
                <span className="ssm-related-name">{c.legalName || `DOT ${c.dotNumber}`}</span>
                <span className="ssm-related-dot">DOT #{c.dotNumber}</span>
                {c.usdotStatus && (
                  <span className={`ssm-related-status ssm-related-status--${String(c.usdotStatus).toLowerCase() === "active" ? "active" : "inactive"}`}>
                    {c.usdotStatus}
                  </span>
                )}
              </div>

              <div className="ssm-related-chips">
                {(c.matchedOn ?? []).map((m) => (
                  <span key={m} className={`ssm-match-chip${STRONG_MATCHES.has(m) ? " ssm-match-chip--strong" : ""}`}>
                    <span className="ssm-match-chip__icon">{MATCH_ICONS[m] ?? "•"}</span>
                    {m}
                  </span>
                ))}
                {(c.depth ?? 1) > 1 && (
                  <span className="ssm-match-chip ssm-match-chip--derived" title="Not reported directly — found by following the relationship chain">
                    indirect connection
                  </span>
                )}
              </div>

              <div className="ssm-related-meta">
                {c.primaryContact && (
                  <span>Officer: <strong>{c.primaryContact}</strong></span>
                )}
                {(c.path ?? []).length > 2 && (
                  <span className="ssm-related-path">via DOT {(c.path ?? []).slice(1, -1).join(" → ")}</span>
                )}
                {(c.sharedVins ?? []).map((v) => (
                  <span key={v.vin} className="ssm-related-vin">
                    Shared {v.unitType || "unit"} VIN {v.vin}
                    {v.lastInspection ? ` · last inspected ${v.lastInspection}` : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="ssm-related-foot">
        A shared officer, e-mail, phone or tax ID usually means the same person runs both
        companies. A shared vehicle is often just a lease or a resale — check which it is before
        you describe it.
        {sources.length > 0 && (
          <><br />Source: {sources.map((s) => SOURCE_LABELS[s] ?? s).join(" · ")}</>
        )}
      </p>
    </div>
  );
};

/* ── Submission Summary Modal ── */
type VerdictLevel = "approve" | "conditions" | "refer" | "decline" | "unknown";

function parseVerdictLevel(text: string): VerdictLevel {
  // Extract only the RECOMMENDATION: line to avoid false matches on words like
  // "Declined" that appear in rulebook guideline row names within the verdict table.
  const match = text.match(/^RECOMMENDATION:\s*(.+)$/im);
  if (!match) return "unknown";
  const rec = match[1].trim().toUpperCase();
  if (rec.includes("UNLIKELY TO BE WRITTEN")) return "decline";
  if (rec.includes("NEEDS FURTHER REVIEW")) return "refer";
  if (rec.includes("ACCEPTABLE WITH CONDITIONS")) return "conditions";
  if (rec.includes("LIKELY ACCEPTABLE")) return "approve";
  return "unknown";
}

const VERDICT_LABELS: Record<VerdictLevel, string> = {
  approve: "LIKELY ACCEPTABLE",
  conditions: "ACCEPTABLE WITH CONDITIONS",
  refer: "NEEDS FURTHER REVIEW",
  decline: "UNLIKELY TO BE WRITTEN",
  unknown: "VERDICT PENDING",
};

const SubmissionSummaryModal: React.FC<{ data: SubmissionSummaryResult | null; loading: boolean; verdict: string | null; verdictLoading: boolean; verdictError: boolean; relatedCarriers: RelatedCarrier[]; relatedFlagged: boolean; onClose: () => void }> = ({ data, loading, verdict, verdictLoading, verdictError, relatedCarriers, relatedFlagged, onClose }) => {

  if (loading || !data) {
    return (
      <div className="ssm-overlay" onClick={onClose}>
        <div className="ssm-loader-box" onClick={(e) => e.stopPropagation()}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5" strokeLinecap="round" className="dr-spin">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <div className="ssm-loader-text">Generating Submission Summary…</div>
          <div className="ssm-loader-sub">Reading documents and evaluating readiness</div>
        </div>
      </div>
    );
  }

  const r = data.report ?? {};
  const overview = r.quoteOverview ?? {};
  const drivers = r.driverSummary ?? [];
  const vehicles = r.vehicleInventory ?? [];
  const lossRun = r.lossRunSummary ?? {};
  const checklist = r.readinessChecklist ?? {};
  const overall = r.overallStatus ?? {};

  const score = data.qualityScore ?? 0;
  const ready = data.canSubmit;
  const scoreColor = score >= 75 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626";
  const isOk = (s: string) => s === "PRESENT" || s === "READY" || s === "PROCESSED";

  const Dot = ({ ok, partial }: { ok: boolean; partial?: boolean }) => (
    <span className={`ssm-dot ssm-dot--${ok ? "ok" : partial ? "partial" : "fail"}`}>
      {ok ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={partial ? "#d97706" : "#dc2626"} strokeWidth="3">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      )}
    </span>
  );

  const StatusPill = ({ value }: { value: string }) => {
    const ok = isOk(value);
    return <span className={`ssm-status-pill ssm-status-pill--${ok ? "ok" : "fail"}`}>{value}</span>;
  };

  return (
    <div className="ssm-overlay" onClick={onClose}>
      <div className="ssm-container" onClick={(e) => e.stopPropagation()}>
        {/* ── Gradient Header ── */}
        <div className="ssm-header">
          <div className="ssm-header__top">
            <div>
              <div className="ssm-header__eyebrow">Carrier Submission Report</div>
              <div className="ssm-header__title">{overview.companyName || "Submission Summary"}</div>
              <div className="ssm-header__id">{data.quoteId}</div>
            </div>
            <button className="ssm-close-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Score row */}
          <div className="ssm-score-row">
            <div className="ssm-score-ring">
              <svg width="36" height="36" viewBox="0 0 36 36" className="ssm-score-svg">
                <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                <circle
                  cx="18" cy="18" r="14" fill="none"
                  stroke={scoreColor}
                  strokeWidth="4"
                  strokeDasharray={`${(score / 100) * 87.96} 87.96`}
                  strokeLinecap="round"
                  className="ssm-score-circle--glow"
                />
              </svg>
              <div>
                <div className="ssm-score-value">{score}%</div>
                <div className="ssm-score-label">Quality Score</div>
              </div>
            </div>

            <span className={`ssm-ready-pill ssm-ready-pill--${ready ? "ok" : "fail"}`}>
              {ready ? "✓ Ready to Submit" : "✗ " + (data.status ?? "").replaceAll("_", " ")}
            </span>

            {Object.entries(data.documents ?? {}).map(([doc, status]) => (
              <span key={doc} className={`ssm-doc-pill ssm-doc-pill--${isOk(status) ? "ok" : "fail"}`}>
                {doc}: {status}
              </span>
            ))}

            {/* With the document pills, so it lands in the same glance as the score. */}
            {(relatedFlagged || relatedCarriers.length > 0) && (
              <span className="ssm-doc-pill ssm-doc-pill--related">
                ⚠ {relatedCarriers.length > 0
                  ? `${relatedCarriers.length} RELATED ${relatedCarriers.length === 1 ? "CARRIER" : "CARRIERS"}`
                  : "RELATED CARRIER FLAGGED"}
              </span>
            )}
          </div>

          {data.summary && (
            <div className="ssm-ai-summary">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{data.summary}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* ── Scrollable Body ── */}
        <div className="ssm-body">

          {/* ── Carrier Verdict ── */}
          {verdictLoading ? (
            <div className="ssm-card ssm-card--verdict-loading">
              <div className="ssm-section-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                Carrier Verdict
              </div>
              <div className="ssm-verdict-loading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" className="dr-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>Evaluating quote against carrier rules…</span>
              </div>
            </div>
          ) : verdict ? (() => {
            const level = parseVerdictLevel(verdict);
            return (
              <div className={`ssm-card ssm-card--verdict ssm-card--verdict-${level}`}>
                <div className="ssm-section-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                  Carrier Verdict
                </div>
                <span className={`ssm-verdict-badge ssm-verdict-badge--${level}`}>
                  {VERDICT_LABELS[level]}
                </span>
                <div className="ssm-verdict-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={verdictMdComponents}>{verdict}</ReactMarkdown>
                </div>
              </div>
            );
          })() : verdictError ? (
            <div className="ssm-card ssm-card--verdict-error">
              <div className="ssm-section-label ssm-section-label--warn">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Carrier Verdict Unavailable
              </div>
              <p className="ssm-verdict-error-msg">
                Verdict could not be generated. Upload at least one document so the carrier graph is built, then click <strong>Submission AI Summary</strong> again.
              </p>
            </div>
          ) : null}

          <RelatedCarriersCard
            carriers={relatedCarriers}
            flagged={relatedFlagged}
            cabChecked={isOk(data.documents?.CAB ?? "")}
          />

          {overview.companyName && (
            <div className="ssm-card">
              <div className="ssm-section-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                Quote Overview
              </div>
              <div className={`ssm-overview-grid${overview.premiumBreakdown ? " ssm-overview-grid--mb" : ""}`}>
                {[
                  ["Company", overview.companyName],
                  ["Location", overview.location],
                  ["Years Established", overview.yearsEstablished],
                  ["Total Premium", overview.totalPremium != null ? `$${Number(overview.totalPremium).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"],
                ].map(([k, v]) => (
                  <div key={String(k)}>
                    <div className="ssm-field-key">{k}</div>
                    <div className="ssm-field-val">{String(v ?? "—")}</div>
                  </div>
                ))}
              </div>
              {overview.premiumBreakdown && (
                <table className="ssm-table">
                  <thead>
                    <tr>
                      <th className="ssm-th">Coverage</th>
                      <th className="ssm-th ssm-th--right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(overview.premiumBreakdown).map(([k, v]) => (
                      <tr key={k}>
                        <td className="ssm-td">{k.replace(/([A-Z])/g, " $1").trim()}</td>
                        <td className="ssm-td ssm-td--right">${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {drivers.length > 0 && (
            <div className="ssm-card">
              <div className="ssm-section-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                Driver Summary &amp; MVR Status
              </div>
              <table className="ssm-table">
                <thead>
                  <tr>
                    {["Driver", "Age", "Role", "MVR Status", "Violations", "Accidents"].map((h) => (
                      <th key={h} className="ssm-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d: any, i: number) => (
                    <tr key={i} className={i % 2 === 0 ? "ssm-tr--even" : "ssm-tr--odd"}>
                      <td className="ssm-td ssm-td--bold">{d.driverName}</td>
                      <td className="ssm-td">{d.age ?? "—"}</td>
                      <td className="ssm-td ssm-td--muted">{d.role || "—"}</td>
                      <td className="ssm-td"><StatusPill value={d.mvrStatus} /></td>
                      <td className="ssm-td ssm-td--center">{d.violations ?? 0}</td>
                      <td className="ssm-td ssm-td--center">{d.accidents ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {vehicles.length > 0 && (
            <div className="ssm-card">
              <div className="ssm-section-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5">
                  <rect x="1" y="3" width="15" height="13" rx="1" />
                  <path d="M16 8h4l3 3v5h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                Vehicle Inventory
              </div>
              <table className="ssm-table">
                <thead>
                  <tr>
                    {["Year", "Make", "Model", "Type", "VIN"].map((h) => (
                      <th key={h} className="ssm-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v: any, i: number) => (
                    <tr key={i} className={i % 2 === 0 ? "ssm-tr--even" : "ssm-tr--odd"}>
                      <td className="ssm-td ssm-td--bold">{v.year}</td>
                      <td className="ssm-td">{v.make}</td>
                      <td className="ssm-td">{v.model || "—"}</td>
                      <td className="ssm-td">{v.vehicleType || "—"}</td>
                      <td className="ssm-td ssm-td--mono">{v.vin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={`ssm-split-grid ssm-split-grid--${lossRun.status && Object.keys(checklist).length > 0 ? "2col" : "1col"}`}>
            {lossRun.status && (
              <div className="ssm-card ssm-card--no-mb">
                <div className="ssm-section-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  Loss Run
                </div>
                <div className="ssm-lossrun-detail">
                  <div>
                    <div className="ssm-lossrun-key">Status</div>
                    <StatusPill value={lossRun.status} />
                  </div>
                  {lossRun.totalClaims != null && (
                    <div>
                      <div className="ssm-lossrun-key">Total Claims</div>
                      <span className="ssm-lossrun-val">{lossRun.totalClaims}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {Object.keys(checklist).length > 0 && (
              <div className="ssm-card ssm-card--no-mb">
                <div className="ssm-section-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5">
                    <polyline points="9 11 12 14 22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                  Readiness Checklist
                </div>
                <div className="ssm-checklist-col">
                  {Object.entries(checklist).map(([k, v]) => {
                    const ok = v === true || v === "true";
                    const partial = typeof v === "string" && v !== "true" && v !== "false";
                    return (
                      <div key={k} className="ssm-checklist-row">
                        <Dot ok={ok} partial={partial} />
                        <span className="ssm-checklist-label">{k.replace(/([A-Z])/g, " $1").trim()}</span>
                        {partial && <span className="ssm-checklist-partial">{String(v)}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {overall.missingItems?.length > 0 && (
            <div className="ssm-card ssm-card--warn">
              <div className="ssm-section-label ssm-section-label--warn">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Missing Items
              </div>
              <div className="ssm-missing-list">
                {overall.missingItems.map((item: string, i: number) => (
                  <div key={i} className="ssm-missing-item">
                    <Dot ok={false} />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Doc Analysis Modal (CAB / SAFERS / Loss Run) ── */
const DocAnalysisModal: React.FC<{
  title: string;
  data: any;
  onClose: () => void;
  /** CAB only - the related-carrier check, shown alongside the analysis. */
  relatedCarriers?: RelatedCarrier[];
  relatedFlagged?: boolean;
  /** SAFERS only - alerts raised on the FMCSA snapshot. */
  safersAlerts?: SafersAlert[];
}> = ({ title, data, onClose, relatedCarriers = [], relatedFlagged = false, safersAlerts = [] }) => {
  const d = data?.data ?? data;
  const relatedSources = Array.from(
    new Set(relatedCarriers.map((c) => c.source).filter(Boolean) as string[]),
  );
  return (
    <div className="dam-overlay" onClick={onClose}>
      <div className="dam-container" onClick={(e) => e.stopPropagation()}>
        <div className="dam-header">
          <span className="dam-title">{title}</span>
          <button className="dam-close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {d?.status && (
          <div className="dam-status-wrap">
            <span className={`dam-status-badge dam-status-badge--${d.status === "processed" ? "processed" : "pending"}`}>
              {String(d.status)}
            </span>
          </div>
        )}

        {safersAlerts.length > 0 && (
          <div className={`dam-safers-alert${safersAlerts.some((a) => a.severity === "high") ? " dam-safers-alert--high" : ""}`}>
            <div className="dam-safers-alert__head">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Needs Review
              <span className="dam-safers-alert__count">{safersAlerts.length}</span>
            </div>
            {safersAlerts.map((a) => (
              <div key={a.code} className={`dam-safers-alert__row${a.severity === "high" ? " dam-safers-alert__row--high" : ""}`}>
                <div className="dam-safers-alert__row-head">
                  <span className="dam-safers-alert__label">{a.label}</span>
                  {(a.values ?? []).map((v) => (
                    <span key={v} className="dam-safers-chip">{v}</span>
                  ))}
                </div>
                {a.message && <div className="dam-safers-alert__msg">{a.message}</div>}
              </div>
            ))}
          </div>
        )}

        {relatedFlagged && (
          <div className={`dam-related${relatedCarriers.some(isStrongMatch) ? " dam-related--high" : ""}`}>
            <div className="dam-related__head">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Related Carriers
              <span className="dam-related__count">{relatedCarriers.length || "!"}</span>
            </div>

            {relatedCarriers.length === 0 ? (
              <p className="dam-related__note">
                A relationship was flagged but the carriers could not be listed. Re-fetch the CAB
                report, or check the report&apos;s Chameleon Carrier page.
              </p>
            ) : (
              <>
                <p className="dam-related__intro">
                  This carrier may be associated with{" "}
                  <strong>{relatedCarriers.length} other trucking {relatedCarriers.length === 1 ? "company" : "companies"}</strong>.
                  The insurance carrier sees the same records and will ask you to explain
                  the connection, so explain it when you submit rather than waiting to be asked.
                </p>

                <div className="dam-related__list">
                  {relatedCarriers.map((c) => (
                    <div key={c.dotNumber} className={`dam-related__row${isStrongMatch(c) ? " dam-related__row--strong" : ""}`}>
                      <div className="dam-related__row-head">
                        <span className="dam-related__name">{c.legalName || `DOT ${c.dotNumber}`}</span>
                        <span className="dam-related__dot">DOT #{c.dotNumber}</span>
                        {c.usdotStatus && (
                          <span className={`dam-related__status dam-related__status--${String(c.usdotStatus).toLowerCase() === "active" ? "active" : "inactive"}`}>
                            {c.usdotStatus}
                          </span>
                        )}
                      </div>

                      <div className="dam-related__chips">
                        {(c.matchedOn ?? []).map((m) => (
                          <span key={m} className={`dam-match-chip${STRONG_MATCHES.has(m) ? " dam-match-chip--strong" : ""}`}>
                            {MATCH_ICONS[m] ?? "•"} {m}
                          </span>
                        ))}
                        {(c.depth ?? 1) > 1 && (
                          <span className="dam-match-chip dam-match-chip--derived">indirect connection</span>
                        )}
                      </div>

                      {c.primaryContact && (
                        <div className="dam-related__meta">Officer: <strong>{c.primaryContact}</strong></div>
                      )}
                      {(c.path ?? []).length > 2 && (
                        <div className="dam-related__meta">via DOT {(c.path ?? []).slice(1, -1).join(" → ")}</div>
                      )}
                      {(c.sharedVins ?? []).map((v) => (
                        <div key={v.vin} className="dam-related__vin">
                          Shared {v.unitType || "unit"} VIN {v.vin}
                          {v.lastInspection ? ` · last inspected ${v.lastInspection}` : ""}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <p className="dam-related__note">
                  A shared officer, e-mail, phone or tax ID usually means the same person runs
                  both companies. A shared vehicle is often just a lease or a resale.
                  {relatedSources.length > 0 && (
                    <><br />Source: {relatedSources.map((x) => SOURCE_LABELS[x] ?? x).join(" · ")}</>
                  )}
                </p>
              </>
            )}
          </div>
        )}

        {d?.summary && (
          <div className="dam-section">
            <div className="dam-label">Summary</div>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{d.summary}</ReactMarkdown>
          </div>
        )}

        {d?.ready !== undefined && (
          <div className="dam-ready-row">
            <span className="dam-ready-key">Ready to Submit</span>
            <span className={`dam-ready-badge dam-ready-badge--${d.ready ? "yes" : "no"}`}>
              {d.ready ? "Yes" : "No"}
            </span>
          </div>
        )}

        {d &&
          Object.entries(d)
            .filter(([k]) => !["status", "summary", "ready", "quoteId", "documentType"].includes(k))
            .map(([k, v]) =>
              v !== null && v !== undefined && v !== "" ? (
                <div key={k} className="dam-field">
                  <div className="dam-field__key">{k.replace(/([A-Z])/g, " $1").trim()}</div>
                  <div className="dam-field__val">{String(v)}</div>
                </div>
              ) : null,
            )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Props
───────────────────────────────────────────── */
interface DataRoomStepProps {
  transactionId: string;
  siDrivers: any[];
  ii: any;
  si: any;
  totalPremium: number;
  totalFees: number;
  quoteId?: string;
  status?: string;
  initialData?: any;
  onBack: () => void;
  onComplete: () => void;
  /** Called with the resolved AI quoteId whenever it becomes known.
   *  QuoteDetailPage uses this to wire the correct quoteId into ChatDrawer. */
  onQuoteIdResolved?: (quoteId: string) => void;
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const DataRoomStep: React.FC<DataRoomStepProps> = ({
  transactionId,
  siDrivers,
  ii,
  si,
  totalPremium,
  totalFees,
  quoteId,
  status,
  initialData,
  onBack,
  onComplete,
  onQuoteIdResolved,
}) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const extractApiError = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    // The API's own message is written for the end user — show it as-is, with no
    // status code. Only fall back to a code when there is nothing to show.
    const serverMsg = data?.detail || data?.message || data?.title;
    if (typeof serverMsg === "string" && serverMsg.trim()) return serverMsg;
    const status = error?.response?.status;
    return status ? `${fallback} (error ${status})` : fallback;
  };

  const handleApiError = (error: any, defaultMsg: string) => {
    if (error?.response?.status === 401) {
      localStorage.clear();
      navigate("/");
      return;
    }
    toast.current?.show({ severity: "error", summary: "Error", detail: extractApiError(error, defaultMsg), life: 4000 });
  };

  /**
   * Returns why an upload was not analyzed, or null when it succeeded.
   * A failed analysis answers 4xx/5xx and is caught by the caller; this covers
   * the 200-with-no-data case, which used to read as a success.
   */
  const extractUploadFailure = (payload: any, docLabel: string): string | null =>
    payload?.data
      ? null
      : payload?.message ?? `${docLabel} was stored but could not be analyzed. Please try uploading it again.`;

  /**
   * Detects a "soft failure" from a DOT-lookup endpoint (cabdotsummary/saferssummary) —
   * HTTP 200 but status:"Failed"/ready:false, with the real reason inside a
   * JSON-stringified `errorMessage` field, e.g. {"detail":"Invalid DOT number..."}.
   * Returns null when the response is a genuine success.
   */
  const extractDotLookupFailure = (payload: any): string | null => {
    if (!payload) return null;
    const failed = payload.status === "Failed" || payload.ready === false;
    if (!failed) return null;
    const raw = payload.errorMessage;
    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.detail === "string" && parsed.detail.trim()) return parsed.detail;
      } catch {
        // Not JSON — show the raw string as-is.
      }
      return raw;
    }
    return "Could not fetch data for this DOT number. Please verify it and try again.";
  };

  /** A restored record (MVR/Loss Run/CAB/SAFERS) that only status:"Failed" excludes — null/"Success"/undefined all restore normally. */
  const isFailedRecord = (r: any): boolean => !!r && (r.status === "Failed" || r.ready === false);

  /* Other docs state */
  const [docs, setDocs] = useState<DocItem[]>(INITIAL_DOCS);
  const submitting = false;

  /* MVR state — single file pool, driver info extracted by AI from uploaded files */
  const [driverMvrs, setDriverMvrs] = useState<DriverMvrState[]>(
    siDrivers.length > 0 ? [{ driverName: "", licenseNumber: "", state: "", files: [], dragOver: false }] : [],
  );

  /* Loss Run multi-file state */
  const [lossRunFiles, setLossRunFiles] = useState<MvrFile[]>([]);
  const [lossRunDragOver, setLossRunDragOver] = useState(false);
  const [lossRunViewModal, setLossRunViewModal] = useState<{ data: any; title: string } | null>(null);

  /* Related ("chameleon") carriers — from CAB, either the report's Chameleon
     Carrier page or the CarrierOK relationship graph. */
  const [relatedCarriers, setRelatedCarriers] = useState<RelatedCarrier[]>([]);
  const [relatedFlagged, setRelatedFlagged] = useState(false);

  /** Adopt relationship data from an AI API submission response or a restored CAB record.
   *  Set-only: two calls race on page load, and letting an empty one erase a found
   *  relationship loses a disclosure the agent has to make. Clearing is explicit. */
  const adoptRelatedCarriers = useCallback((payload: any) => {
    if (!carriesRelationshipFields(payload)) return;
    const carriers = extractRelatedCarriers(payload);
    if (!carriers.length && !hasRelationshipFlag(payload)) return;
    setRelatedCarriers(carriers);
    setRelatedFlagged(true);
  }, []);

  /** Clear before the CAB record is replaced, so the next response starts clean. */
  const resetRelatedCarriers = useCallback(() => {
    setRelatedCarriers([]);
    setRelatedFlagged(false);
  }, []);

  /* SAFER underwriting alerts — the Operation Classification / Carrier Operation /
     Cargo Carried checks on the FMCSA snapshot. */
  const [safersAlerts, setSafersAlerts] = useState<SafersAlert[]>([]);

  /** Set-only, like adoptRelatedCarriers: an empty read must not clear a warning
   *  already on screen. Cleared explicitly when SAFER is re-fetched. */
  const adoptSafersAlerts = useCallback((payload: any) => {
    if (!carriesSafersAlertFields(payload)) return;
    const next = extractSafersAlerts(payload);
    if (!next.length) return;
    setSafersAlerts(next);
  }, []);

  const resetSafersAlerts = useCallback(() => setSafersAlerts([]), []);

  const [cabLoading, setCabLoading] = useState(false);
  const [cabError, setCabError] = useState<string | null>(null);
  const [cabResult, setCabResult] = useState<AnalysisResult | null>(null);
  const [cabFileId, setCabFileId] = useState<string | undefined>(undefined);
  const [cabView, setCabView] = useState<DocViewState>(INIT_VIEW);

  const [safersLoading, setSafersLoading] = useState(false);
  const [safersError, setSafersError] = useState<string | null>(null);
  const [safersResult, setSafersResult] = useState<AnalysisResult | null>(null);
  const [safersFileId, setSafersFileId] = useState<string | undefined>(undefined);
  const [safersView, setSafersView] = useState<DocViewState>(INIT_VIEW);

  /* Delete loading state */
  const [deletingMvr, setDeletingMvr] = useState<Set<string>>(new Set());
  const [deletingLossRun, setDeletingLossRun] = useState<Set<number>>(new Set());
  const [deletingCab, setDeletingCab] = useState(false);
  const [deletingSafers, setDeletingSafers] = useState(false);

  /* Duplicate-filename confirmation queue (MVR upload) — processed one at a time */
  const [mvrDuplicateQueue, setMvrDuplicateQueue] = useState<{ driverIdx: number; file: File }[]>([]);

  /* Delete confirmation modal */
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    title: string;
    fileName: string;
    onConfirm: (() => void) | null;
  }>({ open: false, title: "", fileName: "", onConfirm: null });

  const closeDeleteConfirm = () =>
    setDeleteConfirm({ open: false, title: "", fileName: "", onConfirm: null });

  const [submissionSummary, setSubmissionSummary] = useState<SubmissionSummaryResult | null>(null);
  const [submissionSummaryLoading, setSubmissionSummaryLoading] = useState(false);
  const [rulesVerdict, setRulesVerdict] = useState<string | null>(null);
  const [rulesVerdictLoading, setRulesVerdictLoading] = useState(false);
  const [rulesVerdictError, setRulesVerdictError] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [mvrViewModal, setMvrViewModal] = useState<{ data: any; title: string } | null>(null);
  const [fetchMvrOpen, setFetchMvrOpen] = useState(false);
  const [orderMvrOpen, setOrderMvrOpen] = useState(false);
  const analysisQuoteIdRef = React.useRef<string>("");
  // True once ANY real API response (fast status or LLM summary) has been received.
  // Prevents the component-state quick-summary effect from overriding real data.
  const summaryFromApiRef = React.useRef(false);

  const [editableDotNumber, setEditableDotNumber] = useState<string>(ii.dotNumber || "");

  /* Notes state */
  const [notes, setNotes] = useState<AmsNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);

  /* ── AMS Notes ── */
  const fetchNotes = useCallback(async () => {
    const insuredName = ii?.name ?? "";
    const ncToken = localStorage.getItem("NCToken") ?? "";
    if (!insuredName || !ncToken) return;
    setNotesLoading(true);
    try {
      const token = localStorage.getItem("igtoken");
      const formData = new FormData();
      formData.append("insuredName", insuredName);
      formData.append("nc_token", ncToken);
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getnotes`,
        data: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data?.value ?? []);
    } catch (error: any) {
      handleApiError(error, "Could not load AMS notes.");
    } finally {
      setNotesLoading(false);
    }
  }, [ii?.name]);

  const submitNote = async () => {
    if (!newNoteText.trim()) return;
    const insuredName = ii?.name ?? "";
    const ncToken = localStorage.getItem("NCToken") ?? "";
    if (!insuredName || !ncToken) return;
    setNoteSubmitting(true);
    try {
      const token = localStorage.getItem("igtoken");
      const formData = new FormData();
      formData.append("insuredName", insuredName);
      formData.append("note", newNoteText.trim());
      formData.append("nc_token", ncToken);
      await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/insertnote`,
        data: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewNoteText("");
      await fetchNotes();
    } catch (error: any) {
      handleApiError(error, "Could not save note.");
    } finally {
      setNoteSubmitting(false);
    }
  };

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  /* ── Default summary on mount ────────────────────────────────────────────
     Shows all documents as MISSING immediately so the panel is never blank.
     The initialData effect overrides this with DB data, and the LLM call
     replaces it with the full AI summary once it completes. */
  useEffect(() => {
    setSubmissionSummary((prev) => {
      if (prev) return prev; // DB summary already loaded — keep it
      return {
        documents: { MVR: "MISSING", LOSS_RUN: "MISSING", SAFERS: "MISSING", CAB: "MISSING" },
        qualityScore: 0,
        status: "NOT_READY",
        canSubmit: false,
        summary: "",
        driverMvrStatus: siDrivers.map((d: any) => ({
          driverName: d.name || [d.firstName, d.lastName].filter(Boolean).join(" ") || "Driver",
          mvrStatus: "MISSING",
        })),
      } as any;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Shape the saved MVR rows into card state. Shared by the initial restore and
   *  the refresh after an import, so a newly imported MVR appears without a reload. */
  const toDriverMvrState = (mvr: any[]): DriverMvrState[] =>
    mvr.map((saved: any) => ({
      driverName: saved.driverName ?? "",
      licenseNumber: saved.licenseNumber ?? "",
      state: saved.state ?? "",
      dragOver: false,
      // Only ever restore files that didn't fail — status: null/"Success" show, "Failed" is dropped.
      files: (saved.files ?? []).filter((rf: any) => !isFailedRecord(rf)).map((rf: any) => ({
        file: null,
        fileId: rf.id ?? rf.fileId ?? undefined,
        restoredName: rf.fileName ?? "",
        restoredSize: rf.fileSize ?? 0,
        restoredUrl: rf.fileUrl ?? rf.analysisResult?.fileURL ?? "",
        loading: false,
        error: null,
        result: rf.analysisResult ?? null,
        viewLoading: false,
        viewResult: rf.viewResult ?? null,
        viewOpen: false,
      })),
    }));

  /** Re-read the saved MVRs so an imported document shows straight away. */
  const refreshMvrFiles = async () => {
    const qid = analysisQuoteIdRef.current || quoteId;
    if (!qid) return;
    try {
      const token = localStorage.getItem("igtoken");
      const res = await axios.get(
        `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getsubmission`,
        { params: { quoteId: qid }, headers: { Authorization: `Bearer ${token}` } },
      );
      const mvr = res.data?.mvr;
      if (Array.isArray(mvr) && mvr.length > 0) setDriverMvrs(toDriverMvrState(mvr));
    } catch (error: any) {
      handleApiError(error, "Could not refresh the MVR list.");
    }
  };

  /* ── Hydrate state from previously saved submission (history restore) ── */
  useEffect(() => {
    if (!initialData) return;

    const { documents, mvr, submissionSummary: savedSummary } = initialData;

    if (initialData.quoteId) analysisQuoteIdRef.current = initialData.quoteId;

    // Restore Loss Run / SAFERS / CAB
    if (documents) {
      setDocs((prev) =>
        prev.map((d) => {
          const r = documents[d.id];
          // A failed CAB/SAFERS lookup shouldn't render as an uploaded/checked document either.
          if (!r || isFailedRecord(r) || !r.fileName) return d;
          return {
            ...d,
            restoredName: r.fileName,
            restoredSize: r.fileSize ?? 0,
            restoredUrl: r.fileUrl ?? "",
            status: r.status === "analyzed" || r.analysisResult ? "checked" : "uploaded",
          };
        }),
      );

      const makeLossRunFile = (rf: any): MvrFile => ({
        file: null,
        fileId: rf.id ?? rf.fileId ?? undefined,
        restoredName: rf.fileName ?? "",
        restoredSize: rf.fileSize ?? 0,
        restoredUrl: rf.fileUrl ?? rf.analysisResult?.fileURL ?? "",
        loading: false,
        error: null,
        result: rf.analysisResult ?? null,
        viewLoading: false,
        viewResult: rf.viewResult ?? (rf.summary ? { data: { summary: rf.summary, documentType: "LOSS_RUN", status: "processed", ready: rf.analysisResult?.data?.ready ?? rf.ready ?? false } } : null),
        viewOpen: false,
      });

      // Only ever restore Loss Run entries that didn't fail — status: null/"Success" show, "Failed" is dropped.
      if (Array.isArray(documents.lossRuns) && documents.lossRuns.length > 0) {
        setLossRunFiles(documents.lossRuns.filter((rf: any) => !isFailedRecord(rf)).map(makeLossRunFile));
      } else if (documents.lossRun?.fileName && !isFailedRecord(documents.lossRun)) {
        setLossRunFiles([makeLossRunFile(documents.lossRun)]);
      }

      // A previously-failed CAB lookup shouldn't be restored at all.
      const cabFailed = isFailedRecord(documents.cab);
      if (!cabFailed) {
        if (documents.cab?.analysisResult) setCabResult(documents.cab.analysisResult);
        if (documents.cab?.id) setCabFileId(documents.cab.id);
        // Persisted inside the CAB analysis JSON, so the warning survives a reload.
        adoptRelatedCarriers(documents.cab?.analysisResult ?? documents.cab);
      }

      // A previously-failed SAFERS lookup (status:"Failed"/ready:false) shouldn't be
      // restored at all — only show a SAFERS record once it's actually succeeded.
      const safersFailed = isFailedRecord(documents.safers);
      if (!safersFailed) {
        if (documents.safers?.id) setSafersFileId(documents.safers.id);
        if (documents.safers?.analysisResult) {
          setSafersResult(documents.safers.analysisResult);
        } else if (documents.safers && (documents.safers.summary || documents.safers.fileName)) {
          // API stores SAFERS summary directly — synthesize an AnalysisResult so the View button shows
          setSafersResult({
            success: true,
            fileURL: documents.safers.fileUrl ?? "",
            data: {
              quoteId: initialData.quoteId ?? "",
              documentType: "SAFERS",
              status: "processed",
              summary: documents.safers.summary ?? "",
              ready: true,
            },
          });
        }
        if (documents.safers?.viewResult) setSafersView({ loading: false, result: documents.safers.viewResult, open: false });
      }

      if (!cabFailed && documents.cab?.viewResult) setCabView({ loading: false, result: documents.cab.viewResult, open: false });
    }

    // Restore MVR files — initialize one slot per uploaded MVR entry so all files are visible
    if (Array.isArray(mvr) && mvr.length > 0) {
      setDriverMvrs(toDriverMvrState(mvr));
    }

    if (savedSummary) {
      // Normalize document keys: DB may have "LOSS RUN" (space) from older saves.
      // Convert all space-based keys to underscore so staleness detection and
      // rendering are consistent with the API's REQUIRED_DOCS format.
      const normDocs: Record<string, string> = {};
      for (const [k, v] of Object.entries(savedSummary.documents ?? {})) {
        normDocs[k.replace(/ /g, "_")] = v;
      }
      summaryFromApiRef.current = true;
      setSubmissionSummary({ ...savedSummary, documents: normDocs });
    } else {
      // No saved summary yet — derive READY/MISSING for all 4 required docs from the
      // analysis results we already have, so the panel isn't blank while the LLM runs.
      // Always include all 4 docs so quality score is calculated against the full set.
      const quickDocs: Record<string, string> = {
        MVR: "MISSING", LOSS_RUN: "MISSING", SAFERS: "MISSING", CAB: "MISSING",
      };

      const cabReady = documents?.cab?.analysisResult?.data?.ready ?? (documents?.cab?.analysisResult as any)?.ready;
      if (documents?.cab?.analysisResult || documents?.cab?.fileName)
        quickDocs["CAB"] = cabReady ? "READY" : "MISSING";

      const safersReady = documents?.safers?.analysisResult?.data?.ready ?? (documents?.safers?.analysisResult as any)?.ready;
      if (documents?.safers?.analysisResult || documents?.safers?.summary || documents?.safers?.fileName)
        quickDocs["SAFERS"] = safersReady ? "READY" : "MISSING";

      const lossRunArr = Array.isArray(documents?.lossRuns) ? documents.lossRuns : (documents?.lossRun?.fileName ? [documents.lossRun] : []);
      if (lossRunArr.length > 0) {
        const lrReady = lossRunArr.some((lr: any) => lr.analysisResult?.data?.ready ?? (lr.analysisResult as any)?.ready);
        quickDocs["LOSS_RUN"] = lrReady ? "READY" : "MISSING";
      }

      if (Array.isArray(mvr) && mvr.some((dm: any) => dm.files?.length > 0)) {
        const mvrReady = mvr.some((dm: any) => dm.files?.some((f: any) => f.analysisResult?.data?.ready ?? (f.analysisResult as any)?.ready));
        quickDocs["MVR"] = mvrReady ? "READY" : "MISSING";
      }

      const readyCount = Object.values(quickDocs).filter(s => s === "READY").length;
      setSubmissionSummary({
        documents: quickDocs,
        qualityScore: Math.round((readyCount / 4) * 100),
        status: readyCount === 4 ? "READY" : "NOT_READY",
        canSubmit: readyCount === 4,
        summary: "",
        driverMvrStatus: [],
      } as any);
    }

    // The fast DB-only endpoint (no LLM, under a second) corrects any saved status
    // that is out of sync, and is the only source of the related-carrier check --
    // the Portal's getsubmission payload carries no relationship fields. Hence
    // unconditional: gating it on staleness meant a fully-READY submission never
    // refreshed, so the warning stayed hidden until the user opened the AI Summary.
    if (initialData.quoteId) {
      callSubmissionStatus(initialData.quoteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);


  /* ── Quick summary from component state ──────────────────────────────────
     Safety net for the case where initialData was null or had no analysisResult.
     Runs AFTER the hydration effect has populated cabResult/safersResult/etc.
     Only fires when no LLM-generated summary exists yet (summary string is empty).
     Once the LLM returns a real summary (non-empty string), this never overrides it. */
  useEffect(() => {
    // Once any real API response has been received (fast status or LLM), never override.
    // The summary="" check alone is insufficient because callSubmissionStatus returns summary:"".
    if (summaryFromApiRef.current) return;
    if (submissionSummary?.summary) return; // LLM summary already present — extra guard

    // Always include all 4 required docs so quality score is never inflated.
    const quickDocs: Record<string, string> = {
      MVR: "MISSING", LOSS_RUN: "MISSING", SAFERS: "MISSING", CAB: "MISSING",
    };

    if (cabResult) {
      const r = cabResult?.data?.ready ?? (cabResult as any)?.ready ?? true;
      quickDocs["CAB"] = r ? "READY" : "MISSING";
    }
    if (safersResult) {
      const r = safersResult?.data?.ready ?? (safersResult as any)?.ready ?? true;
      quickDocs["SAFERS"] = r ? "READY" : "MISSING";
    }
    if (lossRunFiles.some((f) => f.result || f.restoredName)) {
      const r = lossRunFiles.some((f) => f.result?.data?.ready ?? (f.result as any)?.ready);
      quickDocs["LOSS_RUN"] = r ? "READY" : "MISSING";
    }
    if (driverMvrs.some((dm) => dm.files.some((f) => f.result || f.restoredName))) {
      const r = driverMvrs.some((dm) => dm.files.some((f) => f.result?.data?.ready ?? (f.result as any)?.ready));
      quickDocs["MVR"] = r ? "READY" : "MISSING";
    }

    const readyCount = Object.values(quickDocs).filter((s) => s === "READY").length;
    setSubmissionSummary((prev) => {
      if (summaryFromApiRef.current) return prev ?? null; // API data already set — never override
      if (prev?.summary) return prev; // LLM summary arrived — keep it
      return {
        documents: quickDocs,
        qualityScore: Math.round((readyCount / 4) * 100),
        status: readyCount === 4 ? "READY" : "NOT_READY",
        canSubmit: readyCount === 4,
        summary: "",
        driverMvrStatus: [],
      } as any;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabResult, safersResult, lossRunFiles, driverMvrs]);

  /* ── Readiness ── */
  const { mvrDocStatus, mvrAnalyzedCount, mvrTotalCount } = React.useMemo(() => {
    const allFiles = driverMvrs.flatMap((dm) => dm.files);
    const totalCount = allFiles.length;
    const analyzedCount = allFiles.filter((f) => f.result || f.restoredName).length;
    const mvrDocStatus: DocStatus = totalCount === 0 ? "missing" : analyzedCount > 0 ? "checked" : "uploaded";
    return { mvrDocStatus, mvrAnalyzedCount: analyzedCount, mvrTotalCount: totalCount };
  }, [driverMvrs]);
  const totalDocCount = docs.length + (siDrivers.length > 0 ? 1 : 0);
  const checkedCount = docs.filter((d) => d.status !== "missing").length + (mvrDocStatus !== "missing" ? 1 : 0);
  const readinessPct = totalDocCount > 0 ? Math.round((checkedCount / totalDocCount) * 100) : 0;

  const tier = (() => {
    if (readinessPct <= 25) return { cls: "poor" };
    if (readinessPct <= 50) return { cls: "fair" };
    if (readinessPct <= 75) return { cls: "good" };
    return { cls: "great" };
  })();

  const getDocPillStatus = (apiKey: string, localIsAnalyzed: boolean, localDocStatus: string) => {
    const raw = submissionSummary?.documents?.[apiKey];
    const api = raw != null ? String(raw) : undefined;
    if (api === "PROCESSED") return { label: "Processed", cls: "checked" };
    if (api === "READY") return { label: "Ready", cls: "checked" };
    if (api === "MISSING") return { label: "Missing", cls: "missing" };
    if (api) return { label: api.charAt(0) + api.slice(1).toLowerCase(), cls: "uploaded" };
    if (localIsAnalyzed) return { label: "Processed", cls: "checked" };
    if (localDocStatus === "uploaded") return { label: "Uploaded", cls: "uploaded" };
    return { label: "Missing", cls: "missing" };
  };

  /**
   * File name as stored in blob storage. The API sanitizes names on upload, so the
   * local File name can differ — look documents up by what was actually saved.
   */
  const storedFileName = (blobUrl?: string, localName?: string): string | undefined =>
    blobUrl ? blobUrl.split("?")[0].split("/").pop() || localName : localName;

  /* ── Generic GET view call ── */
  const fetchDocView = async (quoteIdVal: string, fileName: string) => {
    const token = localStorage.getItem("igtoken");
    const res = await axios({
      method: "get",
      url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/document-summary`,
      params: { quoteId: quoteIdVal, fileName },
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  /* ── View handlers ── */
  const updateMvrFile = (driverIdx: number, fileIdx: number, patch: Partial<MvrFile>) =>
    setDriverMvrs((prev) =>
      prev.map((dm, i) =>
        i !== driverIdx
          ? dm
          : {
            ...dm,
            files: dm.files.map((f, j) => (j === fileIdx ? { ...f, ...patch } : f)),
          },
      ),
    );

  const updateLossRunFile = (fileIdx: number, patch: Partial<MvrFile>) =>
    setLossRunFiles((prev) => prev.map((f, j) => (j === fileIdx ? { ...f, ...patch } : f)));

  const viewMVRAnalysis = async (driverIdx: number, fileIdx: number) => {
    const dm = driverMvrs[driverIdx];
    const mf = dm?.files[fileIdx];
    if (!mf) return;
    if (mf.viewResult) {
      setMvrViewModal({ data: mf.viewResult, title: "MVR" });
      return;
    }
    updateMvrFile(driverIdx, fileIdx, { viewLoading: true });
    try {
      const qid = mf.result?.data?.quoteId ?? (mf.result as any)?.quoteId ?? analysisQuoteIdRef.current ?? quoteId;
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "get",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getmvrsummary`,
        params: {
          quoteId: qid,
          fileName: storedFileName(mf.result?.fileURL ?? mf.restoredUrl, mf.file?.name ?? mf.restoredName),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      updateMvrFile(driverIdx, fileIdx, { viewLoading: false, viewResult: res.data });
      setMvrViewModal({ data: res.data, title: "MVR" });
    } catch (error: any) {
      updateMvrFile(driverIdx, fileIdx, { viewLoading: false });
      handleApiError(error, "Could not load MVR analysis.");
    }
  };

  const viewDocAnalysis = async (
    result: AnalysisResult | null,
    file: File | null,
    view: DocViewState,
    setView: React.Dispatch<React.SetStateAction<DocViewState>>,
  ) => {
    if (view.open) {
      setView((v) => ({ ...v, open: false }));
      return;
    }
    if (view.result) {
      setView((v) => ({ ...v, open: true }));
      return;
    }
    setView((v) => ({ ...v, loading: true }));
    try {
      const qid = result?.data?.quoteId ?? (result as any)?.quoteId ?? analysisQuoteIdRef.current ?? quoteId;
      const data = await fetchDocView(qid, file?.name ?? "");
      setView({ loading: false, result: data, open: true });
    } catch (error: any) {
      setView((v) => ({ ...v, loading: false }));
      handleApiError(error, "Could not load document analysis.");
    }
  };

  /* ── CAB View ── */
  const viewCabAnalysis = async () => {
    if (cabView.open) {
      setCabView((v) => ({ ...v, open: false }));
      return;
    }
    if (cabView.result) {
      setCabView((v) => ({ ...v, open: true }));
      return;
    }
    setCabView((v) => ({ ...v, loading: true }));
    try {
      const qid = cabResult?.data?.quoteId ?? (cabResult as any)?.quoteId ?? analysisQuoteIdRef.current ?? quoteId;
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "get",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getcabsummary`,
        params: { quoteId: qid },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCabView({ loading: false, result: res.data, open: true });
    } catch (error: any) {
      setCabView((v) => ({ ...v, loading: false }));
      handleApiError(error, "Could not load CAB analysis.");
    }
  };

  /* ── Loss Run View (per-file) ── */
  const viewLossRunAnalysis = async (fileIdx: number) => {
    const lf = lossRunFiles[fileIdx];
    if (!lf) return;
    if (lf.viewResult) {
      setLossRunViewModal({ data: lf.viewResult, title: "Loss Run Analysis" });
      return;
    }
    updateLossRunFile(fileIdx, { viewLoading: true });
    try {
      const qid = lf.result?.data?.quoteId ?? (lf.result as any)?.quoteId ?? analysisQuoteIdRef.current ?? quoteId;
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "get",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getlossrunsummary`,
        params: { quoteId: qid },
        headers: { Authorization: `Bearer ${token}` },
      });
      updateLossRunFile(fileIdx, { viewLoading: false, viewResult: res.data });
      setLossRunViewModal({ data: res.data, title: "Loss Run Analysis" });
    } catch (error: any) {
      updateLossRunFile(fileIdx, { viewLoading: false });
      handleApiError(error, "Could not load Loss Run analysis.");
    }
  };

  /* ── SAFERS View ── */
  const viewSafersAnalysis = async () => {
    if (safersView.open) {
      setSafersView((v) => ({ ...v, open: false }));
      return;
    }
    if (safersView.result) {
      setSafersView((v) => ({ ...v, open: true }));
      return;
    }
    setSafersView((v) => ({ ...v, loading: true }));
    try {
      const qid = safersResult?.data?.quoteId ?? (safersResult as any)?.quoteId ?? analysisQuoteIdRef.current ?? quoteId;
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "get",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getsaferssummary`,
        params: { quoteId: qid },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSafersView({ loading: false, result: res.data, open: true });
    } catch (error: any) {
      setSafersView((v) => ({ ...v, loading: false }));
      handleApiError(error, "Could not load SAFERS analysis.");
    }
  };

  /* ── Delete handlers ── */
  const deleteMVRFile = (driverIdx: number, fileIdx: number) => {
    const mf = driverMvrs[driverIdx]?.files[fileIdx];
    const filePath = mf?.result?.fileURL ?? mf?.restoredUrl ?? "";
    const fileId = mf?.fileId;
    const fileName = mf?.file?.name ?? mf?.restoredName ?? "this document";
    if (!filePath && !fileId) {
      toast.current?.show({ severity: "error", summary: "Delete Failed", detail: "Could not resolve document path. Please re-upload the document to delete it.", life: 5000 });
      return;
    }
    setDeleteConfirm({
      open: true,
      title: "Delete MVR Document",
      fileName,
      onConfirm: async () => {
        closeDeleteConfirm();
        const qid = analysisQuoteIdRef.current || quoteId;
        const key = `${driverIdx}-${fileIdx}`;
        setDeletingMvr((prev) => new Set(prev).add(key));
        try {
          const token = localStorage.getItem("igtoken");
          await axios({
            method: "delete",
            url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/mvr/${qid}`,
            data: { quoteId: qid, filePath, fileId },
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setDriverMvrs((prev) =>
            prev.map((dm, i) =>
              i !== driverIdx ? dm : { ...dm, files: dm.files.filter((_, j) => j !== fileIdx) }
            )
          );
          callSubmissionSummary(qid);
          toast.current?.show({ severity: "success", summary: "Deleted", detail: `"${fileName}" has been removed.`, life: 3000 });
        } catch (error: any) {
          handleApiError(error, "Could not delete MVR document.");
        } finally {
          setDeletingMvr((prev) => { const s = new Set(prev); s.delete(key); return s; });
        }
      },
    });
  };

  const deleteLossRunFile = (fileIdx: number) => {
    const lf = lossRunFiles[fileIdx];
    const filePath = lf?.result?.fileURL ?? lf?.restoredUrl ?? "";
    const fileId = lf?.fileId;
    const fileName = lf?.file?.name ?? lf?.restoredName ?? "this document";
    if (!filePath && !fileId) {
      toast.current?.show({ severity: "error", summary: "Delete Failed", detail: "Could not resolve document path. Please re-upload the document to delete it.", life: 5000 });
      return;
    }
    setDeleteConfirm({
      open: true,
      title: "Delete Loss Run Document",
      fileName,
      onConfirm: async () => {
        closeDeleteConfirm();
        const qid = analysisQuoteIdRef.current || quoteId;
        setDeletingLossRun((prev) => new Set(prev).add(fileIdx));
        try {
          const token = localStorage.getItem("igtoken");
          await axios({
            method: "delete",
            url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/loss-run/${qid}`,
            data: { quoteId: qid, filePath, fileId },
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setLossRunFiles((prev) => prev.filter((_, i) => i !== fileIdx));
          callSubmissionSummary(qid);
          toast.current?.show({ severity: "success", summary: "Deleted", detail: `"${fileName}" has been removed.`, life: 3000 });
        } catch (error: any) {
          handleApiError(error, "Could not delete Loss Run document.");
        } finally {
          setDeletingLossRun((prev) => { const s = new Set(prev); s.delete(fileIdx); return s; });
        }
      },
    });
  };

  const deleteCABFile = (fileName: string) => {
    const cabDoc = docs.find((d) => d.id === "cab");
    const filePath = cabResult?.fileURL ?? cabDoc?.restoredUrl ?? "";
    const fileId = cabFileId;
    if (!filePath && !fileId) {
      toast.current?.show({ severity: "error", summary: "Delete Failed", detail: "Could not resolve document path. Please re-upload the document to delete it.", life: 5000 });
      return;
    }
    setDeleteConfirm({
      open: true,
      title: "Delete CAB Report",
      fileName,
      onConfirm: async () => {
        closeDeleteConfirm();
        const qid = analysisQuoteIdRef.current || quoteId;
        setDeletingCab(true);
        try {
          const token = localStorage.getItem("igtoken");
          await axios({
            method: "delete",
            url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/cab/${qid}`,
            data: { quoteId: qid, filePath, fileId },
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setCabResult(null);
          setCabFileId(undefined);
          setCabView(INIT_VIEW);
          setCabError(null);
          // CAB is the only source, so the warning goes with the record.
          resetRelatedCarriers();
          setDocs((prev) => prev.map((d) => d.id === "cab" ? { ...d, file: null, restoredName: undefined, status: "missing" } : d));
          callSubmissionSummary(qid);
          toast.current?.show({ severity: "success", summary: "Deleted", detail: `"${fileName}" has been removed.`, life: 3000 });
        } catch (error: any) {
          handleApiError(error, "Could not delete CAB report.");
        } finally {
          setDeletingCab(false);
        }
      },
    });
  };

  const deleteSAFERSFile = () => {
    const safersDoc = docs.find((d) => d.id === "safers");
    const filePath = safersResult?.fileURL ?? safersDoc?.restoredUrl ?? "";
    const fileId = safersFileId;
    if (!filePath && !fileId) {
      toast.current?.show({ severity: "error", summary: "Delete Failed", detail: "Could not resolve document path. Please re-upload the document to delete it.", life: 5000 });
      return;
    }
    setDeleteConfirm({
      open: true,
      title: "Delete SAFERS Record",
      fileName: `DOT #${editableDotNumber}`,
      onConfirm: async () => {
        closeDeleteConfirm();
        const qid = analysisQuoteIdRef.current || quoteId;
        setDeletingSafers(true);
        try {
          const token = localStorage.getItem("igtoken");
          await axios({
            method: "delete",
            url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/safers/${qid}`,
            data: { quoteId: qid, filePath, fileId },
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setSafersResult(null);
          setSafersFileId(undefined);
          setSafersView(INIT_VIEW);
          setSafersError(null);
          setDocs((prev) => prev.map((d) => d.id === "safers" ? { ...d, status: "missing" } : d));
          callSubmissionSummary(qid);
          toast.current?.show({ severity: "success", summary: "Deleted", detail: `SAFERS record for DOT #${editableDotNumber} has been removed.`, life: 3000 });
        } catch (error: any) {
          handleApiError(error, "Could not delete SAFERS record.");
        } finally {
          setDeletingSafers(false);
        }
      },
    });
  };

  /* ── Submission Summary ── */
  const normalizeViolations = (v: any): { description: string; date: string }[] => {
    if (!v || v === "null" || v === "") return [];
    if (Array.isArray(v)) {
      return v.map((item: any) =>
        typeof item === "string" ? { description: item, date: "" } : { description: item?.description ?? String(item), date: item?.date ?? "" },
      );
    }
    if (typeof v === "string") {
      return v
        .split(",")
        .map((s) => ({ description: s.trim(), date: "" }))
        .filter((x) => x.description);
    }
    return [];
  };

  const buildQuoteContext = React.useCallback(
    () => ({
      companyName: ii?.name ?? "",
      location: [ii?.city, ii?.state].filter(Boolean).join(", "),
      yearsEstablished: Number(ii?.experienceYears ?? 0),
      premiums: {
        alPremium: Number(si?.premiums?.alPremium ?? 0),
        umPremium: Number(si?.premiums?.umPremium ?? 0),
        policyFee: Number(si?.fees?.policyFee ?? 0),
        taxes: Number(si?.fees?.stateTax ?? 0) + Number(si?.fees?.domicileTax ?? 0),
        totalPremium: Number(totalPremium ?? 0),
      },
      drivers: siDrivers.map((d: any) => ({
        name: d.name ?? "",
        age: Number(d.age ?? 0),
        role: d.role ?? "",
        violations: normalizeViolations(d.violations),
        accidentCount: Number(d.accidentCount ?? 0),
      })),
      vehicles: (si?.vehicles ?? []).map((v: any) => ({
        year: Number(v.year ?? 0),
        make: v.make ?? "",
        model: v.model ?? "",
        vehicleType: v.vehicleType ?? "",
        vin: v.vin ?? "",
      })),
      claims: si?.claims ?? [],
    }),
    [ii, si, siDrivers, totalPremium],
  );

  /** Fire-and-forget: build the carrier knowledge graph after every upload.
   *  Silently swallowed on failure so it never breaks the existing upload flow. */
  const triggerGraphBuild = async (qid: string) => {
    if (!qid) return;
    onQuoteIdResolved?.(qid);
    try {
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/graph/sync`,
        data: { quoteId: qid },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === "failed") {
        console.warn("[CarrierGraph] Fast build returned failed status for quoteId:", qid, res.data?.message);
      }
    } catch (err: unknown) {
      // Non-critical — never affects upload UX, but log for debugging
      console.warn("[CarrierGraph] Fast build request failed for quoteId:", qid, err instanceof Error ? err.message : err);
    }
  };

  /* Fast DB-only status refresh — no LLM, instant response.
     Called on Data Room entry to fix stale summary without waiting 10-30s. */
  const callSubmissionStatus = async (qid?: string) => {
    if (qid) analysisQuoteIdRef.current = qid;
    const resolvedId = analysisQuoteIdRef.current || quoteId || transactionId;
    if (!resolvedId) return;
    try {
      setSubmissionSummaryLoading(true);
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "get",
        url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/submission-status`,
        params: { quoteId: resolvedId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const statusDocs: Record<string, string> = {};
      for (const [k, v] of Object.entries(res.data.documents ?? {})) {
        statusDocs[k.replace(/ /g, "_")] = v;
      }
      adoptRelatedCarriers(res.data);
      adoptSafersAlerts(res.data);
      summaryFromApiRef.current = true; // block component-state effect from overriding
      setSubmissionSummary((prev) => ({
        ...(prev ?? {}),
        ...res.data,
        documents: statusDocs,
        report: prev?.report ?? res.data.report ?? null,
        reportMarkdown: prev?.reportMarkdown ?? res.data.reportMarkdown ?? "",
      } as any));
    } catch (error: any) {
      handleApiError(error, "Could not refresh submission status.");
    } finally {
      setSubmissionSummaryLoading(false);
    }
  };

  const callSubmissionSummary = async (qid?: string) => {
    if (qid) analysisQuoteIdRef.current = qid;
    const resolvedId = analysisQuoteIdRef.current || quoteId || transactionId;
    if (!resolvedId) return;
    try {
      setSubmissionSummaryLoading(true);
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/submission-summary`,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        data: { quoteId: resolvedId, quoteContext: buildQuoteContext() },
      });
      const summaryDocs: Record<string, string> = {};
      for (const [k, v] of Object.entries(res.data.documents ?? {})) {
        summaryDocs[k.replace(/ /g, "_")] = v;
      }
      adoptRelatedCarriers(res.data);
      adoptSafersAlerts(res.data);
      summaryFromApiRef.current = true;
      setSubmissionSummary({ ...res.data, documents: summaryDocs });
    } catch (error: any) {
      handleApiError(error, "Could not generate AI Summary.");
      setSummaryModalOpen(false);
    } finally {
      setSubmissionSummaryLoading(false);
    }
  };

  const callRulesVerdict = async () => {
    const resolvedId = analysisQuoteIdRef.current || quoteId || transactionId;
    if (!resolvedId) return;
    setRulesVerdict(null);
    setRulesVerdictError(false);
    setRulesVerdictLoading(true);
    try {
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/rules/verdict`,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        data: { quoteId: resolvedId },
      });
      setRulesVerdict(res.data.verdict ?? null);
    } catch {
      setRulesVerdictError(true);
    } finally {
      setRulesVerdictLoading(false);
    }
  };

  /* ── MVR Upload (per-driver, per-file) ── */
  const uploadMVRToAPI = async (driverIdx: number, fileIdx: number, fileOverride?: File) => {
    const dm = driverMvrs[driverIdx];
    const mf = dm?.files[fileIdx];
    const file = fileOverride ?? mf?.file;
    if (!file) return;
    if (file.size === 0) {
      updateMvrFile(driverIdx, fileIdx, { loading: false, error: "File is empty (0 bytes). Please upload a valid, non-corrupted PDF." });
      return;
    }
    updateMvrFile(driverIdx, fileIdx, { loading: true, error: null });
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("transactionId", transactionId);
      const insuredName = ii?.name ?? "";
      const ncToken = localStorage.getItem("NCToken") ?? "";
      if (insuredName) formData.append("insuredName", insuredName);
      if (ncToken) formData.append("nc_token", ncToken);
      const token = localStorage.getItem("igtoken");
      const mvrRes = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/mvrsummary`,
        data: formData,
        params: { transactionId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const failure = extractUploadFailure(mvrRes.data, "MVR");
      if (failure) {
        console.error("[MVR] Upload was not analyzed — response:", mvrRes.data);
        updateMvrFile(driverIdx, fileIdx, { loading: false, error: failure });
        toast.current?.show({ severity: "error", summary: "MVR Not Processed", detail: failure, life: 6000 });
        return;
      }
      updateMvrFile(driverIdx, fileIdx, { loading: false, result: mvrRes.data });
      const mvrQid = mvrRes.data?.data?.quoteId ?? mvrRes.data?.quoteId;
      callSubmissionSummary(mvrQid);
      if (mvrQid) triggerGraphBuild(mvrQid);
    } catch (error: any) {
      updateMvrFile(driverIdx, fileIdx, { loading: false, error: extractApiError(error, "Failed to upload MVR") });
      handleApiError(error, "MVR upload failed.");
    }
  };

  /* ── Loss Run Upload (per-file) ── */
  const uploadLossRunToAPI = async (fileIdx: number, fileOverride?: File) => {
    const lf = lossRunFiles[fileIdx];
    const file = fileOverride ?? lf?.file;
    if (!file) return;
    if (file.size === 0) {
      updateLossRunFile(fileIdx, { loading: false, error: "File is empty (0 bytes). Please upload a valid, non-corrupted PDF." });
      return;
    }
    updateLossRunFile(fileIdx, { loading: true, error: null });
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("transactionId", transactionId);
      const insuredName = ii?.name ?? "";
      const ncToken = localStorage.getItem("NCToken") ?? "";
      if (insuredName) formData.append("insuredName", insuredName);
      if (ncToken) formData.append("nc_token", ncToken);
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/lossrunsummary`,
        data: formData,
        params: { transactionId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const failure = extractUploadFailure(res.data, "Loss Run");
      if (failure) {
        console.error("[LossRun] Upload was not analyzed — response:", res.data);
        updateLossRunFile(fileIdx, { loading: false, error: failure });
        toast.current?.show({ severity: "error", summary: "Loss Run Not Processed", detail: failure, life: 6000 });
        return;
      }
      updateLossRunFile(fileIdx, { loading: false, result: res.data });
      const lrQid = res.data?.data?.quoteId ?? res.data?.quoteId;
      callSubmissionSummary(lrQid);
      if (lrQid) triggerGraphBuild(lrQid);
    } catch (error: any) {
      updateLossRunFile(fileIdx, { loading: false, error: extractApiError(error, "Failed to upload loss run") });
      handleApiError(error, "Loss Run upload failed.");
    }
  };

  /* ── Loss Run file handlers ── */
  const addLossRunFiles = (incoming: FileList | File[]) => {
    const newFiles: MvrFile[] = [];
    for (const f of Array.from(incoming)) {
      if (ALLOWED_TYPES.includes(f.type))
        newFiles.push({ file: f, loading: false, error: null, result: null, viewLoading: false, viewResult: null, viewOpen: false });
    }
    if (!newFiles.length) return;
    const startIdx = lossRunFiles.length;
    setLossRunFiles((prev) => [...prev, ...newFiles]);
    newFiles.forEach((lf, i) => uploadLossRunToAPI(startIdx + i, lf.file!));
  };

  const handleLossRunFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addLossRunFiles(e.target.files);
    e.target.value = "";
  };
  const handleLossRunDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setLossRunDragOver(true);
  }, []);
  const handleLossRunDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setLossRunDragOver(false);
  }, []);
  const handleLossRunDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files?.length) addLossRunFiles(Array.from(files));
  };

  /* ── CAB Upload ── */
  const uploadCABToAPI = async (file: File) => {
    try {
      setCabLoading(true);
      setCabError(null);
      resetRelatedCarriers(); // this upload replaces the CAB record
      const formData = new FormData();
      formData.append("file", file);
      formData.append("transactionId", transactionId);
      const insuredName = ii?.name ?? "";
      const ncToken = localStorage.getItem("NCToken") ?? "";
      if (insuredName) formData.append("insuredName", insuredName);
      if (ncToken) formData.append("nc_token", ncToken);
      const token = localStorage.getItem("igtoken");
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/cabsummary`,
        data: formData,
        params: { transactionId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const failure = extractUploadFailure(res.data, "CAB");
      if (failure) {
        console.error("[CAB] Upload was not analyzed — response:", res.data);
        setCabError(failure);
        toast.current?.show({ severity: "error", summary: "CAB Not Processed", detail: failure, life: 6000 });
        return;
      }
      setCabResult(res.data);
      const cabId = res.data?.id ?? res.data?.data?.id;
      if (cabId) setCabFileId(cabId);
      const cabQid = res.data?.data?.quoteId ?? res.data?.quoteId;
      callSubmissionSummary(cabQid);
      if (cabQid) triggerGraphBuild(cabQid);
    } catch (error: any) {
      setCabError(extractApiError(error, "Failed to upload CAB"));
      handleApiError(error, "CAB upload failed.");
    } finally {
      setCabLoading(false);
    }
  };

  /* ── CAB Fetch by DOT ── */
  const fetchCABByDot = async () => {
    try {
      setCabLoading(true);
      setCabError(null);
      resetRelatedCarriers(); // this fetch replaces the CAB record
      const token = localStorage.getItem("igtoken");

      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/cabdotsummary`,
        params: { dotNumber: editableDotNumber, transactionId },
        headers: { Authorization: `Bearer ${token}` },
      });

      const failure = extractDotLookupFailure(res.data);
      if (failure) {
        setCabError(failure);
        toast.current?.show({ severity: "error", summary: "CAB Fetch Failed", detail: failure, life: 6000 });
        return;
      }

      setCabResult(res.data);
      const cabId = res.data?.id ?? res.data?.data?.id;
      if (cabId) setCabFileId(cabId);
      setDocs((prev) => prev.map((d) => (d.id === "cab" ? { ...d, status: "checked" } : d)));

      const cabQid =
        (res.data?.quoteId ?? res.data?.data?.quoteId)
        || analysisQuoteIdRef.current
        || transactionId;
      callSubmissionSummary(cabQid);
      if (cabQid) triggerGraphBuild(cabQid);

    } catch (error: any) {
      setCabError(extractApiError(error, "Failed to fetch CAB data"));
      handleApiError(error, "CAB fetch failed.");
    } finally {
      setCabLoading(false);
    }
  };

  /* ── SAFERS Upload ── */
  const uploadSAFERSToAPI = async () => {
    try {
      setSafersLoading(true);
      setSafersError(null);
      resetSafersAlerts(); // this fetch replaces the SAFER record
      const token = localStorage.getItem("igtoken");
      const formData = new FormData();
      formData.append("dotNumber", editableDotNumber);
      formData.append("transactionId", transactionId);
      const insuredName = ii?.name ?? "";
      const ncToken = localStorage.getItem("NCToken") ?? "";
      if (insuredName) formData.append("insuredName", insuredName);
      if (ncToken) formData.append("nc_token", ncToken);

      // Step 1 — Fetch SAFER from main portal API
      const res = await axios({
        method: "post",
        url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/saferssummary`,
        data: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const failure = extractDotLookupFailure(res.data);
      if (failure) {
        setSafersError(failure);
        toast.current?.show({ severity: "error", summary: "SAFERS Fetch Failed", detail: failure, life: 6000 });
        return;
      }

      setSafersResult(res.data);
      const safersId = res.data?.id ?? res.data?.data?.id;
      if (safersId) setSafersFileId(safersId);
      setDocs((prev) => prev.map((d) => (d.id === "safers" ? { ...d, status: "checked" } : d)));

      // Resolve quoteId — use response value OR fall back to already-known quoteId
      const safersQid =
        (res.data?.quoteId ?? res.data?.data?.quoteId)
        || analysisQuoteIdRef.current
        || transactionId;
      callSubmissionSummary(safersQid);

      // Step 2 — Save SAFER data into TruckingSAFERSResults via our AI API.
      // Must complete BEFORE triggerGraphBuild so build_graph_fast finds SAFER data.
      const dotToUse = editableDotNumber?.trim();
      if (safersQid && dotToUse) {
        try {
          const aiRes = await axios({
            method: "post",
            url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/safers`,
            data: { quoteId: safersQid, dotNumber: dotToUse },
            headers: { Authorization: `Bearer ${token}` },
          });
          // The only place the SAFER alerts arrive immediately - the Portal's
          // saferssummary passes through a DTO that drops them.
          adoptSafersAlerts(aiRes.data);
        } catch (saferErr: any) {
          // Log so we can debug — graph will show SAFER as missing if this fails
          console.warn(
            "[SAFER] AI SAFER save failed — quoteId:", safersQid,
            "dotNumber:", dotToUse,
            "error:", saferErr?.response?.data?.detail || saferErr?.message
          );
        }
      } else {
        console.warn("[SAFER] Skipping AI SAFER save — safersQid:", safersQid, "dotNumber:", dotToUse);
      }

      // Step 3 — Build graph (now TruckingSAFERSResults should have data)
      triggerGraphBuild(safersQid);

    } catch (error: any) {
      setSafersError(extractApiError(error, "Failed to fetch SAFERS"));
      handleApiError(error, "SAFERS fetch failed.");
    } finally {
      setSafersLoading(false);
    }
  };

  /* ── File handlers (other docs) ── */
  const applyFile = (docId: string, file: File | null) => {
    if (file && !ALLOWED_TYPES.includes(file.type)) return;
    setDocs((prev) =>
      prev.map((d) =>
        d.id === docId ? { ...d, file, status: file ? "uploaded" : "missing", dragOver: false, restoredName: undefined, restoredUrl: undefined } : d,
      ),
    );
    if (file) {
      if (docId === "cab") {
        setCabResult(null);
        setCabView(INIT_VIEW);
        uploadCABToAPI(file);
      }
      if (docId === "safers") {
        setSafersResult(null);
        setSafersView(INIT_VIEW);
      }
    }
  };

  const handleFileChange = (docId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(docId, e.target.files?.[0] ?? null);
    e.target.value = "";
  };
  const handleDragOver = useCallback((docId: string, e: React.DragEvent) => {
    e.preventDefault();
    setDocs((prev) => prev.map((d) => ({ ...d, dragOver: d.id === docId })));
  }, []);
  const handleDragLeave = useCallback(() => {
    setDocs((prev) => prev.map((d) => ({ ...d, dragOver: false })));
  }, []);
  const handleDrop = (docId: string, e: React.DragEvent) => {
    e.preventDefault();
    applyFile(docId, e.dataTransfer.files?.[0] ?? null);
  };


  /* ── File handlers (per-driver MVR) ── */

  /* Adds files straight to state and uploads each — the existing/unconfirmed flow,
     used both for fresh (non-duplicate) filenames and after a duplicate is confirmed. */
  const uploadNewMvrFiles = (driverIdx: number, files: File[]) => {
    if (!files.length) return;
    const newFiles: MvrFile[] = files.map((f) => ({
      file: f, loading: false, error: null, result: null, viewLoading: false, viewResult: null, viewOpen: false,
    }));
    const startIdx = driverMvrs[driverIdx]?.files.length ?? 0;
    setDriverMvrs((prev) => prev.map((dm, i) => (i === driverIdx ? { ...dm, files: [...dm.files, ...newFiles], dragOver: false } : dm)));
    newFiles.forEach((mf, i) => uploadMVRToAPI(driverIdx, startIdx + i, mf.file!));
  };

  const addMvrFiles = (driverIdx: number, incoming: FileList | File[]) => {
    // Names already shown across every driver's MVR list (all files, not just this driver's).
    const existingNames = new Set(
      driverMvrs.flatMap((dm) => dm.files.map((f) => (f.file?.name ?? f.restoredName ?? "").toLowerCase()))
    );

    const freshFiles: File[] = [];
    const duplicateFiles: File[] = [];
    for (const f of Array.from(incoming)) {
      if (!ALLOWED_TYPES.includes(f.type)) continue;
      if (existingNames.has(f.name.toLowerCase())) duplicateFiles.push(f);
      else freshFiles.push(f);
    }

    // Case 2 — different filename: existing flow, no confirmation dialog.
    uploadNewMvrFiles(driverIdx, freshFiles);

    // Case 1 — same filename as an already-listed file: confirm before uploading.
    // "Yes" runs the same upload flow above; "No" drops the file — mvrsummary is never called.
    if (duplicateFiles.length) {
      setMvrDuplicateQueue((prev) => [...prev, ...duplicateFiles.map((file) => ({ driverIdx, file }))]);
    }
  };

  const confirmMvrDuplicateUpload = () => {
    const [current, ...rest] = mvrDuplicateQueue;
    if (!current) return;
    setMvrDuplicateQueue(rest);
    uploadNewMvrFiles(current.driverIdx, [current.file]);
  };

  const cancelMvrDuplicateUpload = () => {
    setMvrDuplicateQueue((prev) => prev.slice(1));
  };

  const handleMvrFileChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addMvrFiles(idx, e.target.files);
    e.target.value = "";
  };
  const handleMvrDragOver = useCallback((idx: number, e: React.DragEvent) => {
    e.preventDefault();
    setDriverMvrs((prev) => prev.map((dm, i) => ({ ...dm, dragOver: i === idx })));
  }, []);
  const handleMvrDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDriverMvrs((prev) => prev.map((dm) => ({ ...dm, dragOver: false })));
    }
  }, []);
  const handleMvrDrop = (idx: number, e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files?.length) addMvrFiles(idx, Array.from(files));
  };


  const spinnerSvg = (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="dr-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
  const uploadSvg = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
  const eyeSvg = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
  const trashSvg = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );


  /* ─── render ─── */
  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.open && (
        <div className="dam-overlay" onClick={closeDeleteConfirm}>
          <div className="dr-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="dam-header">
              <span className="dam-title">{deleteConfirm.title}</span>
              <button className="dam-close-btn" onClick={closeDeleteConfirm}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p className="dr-confirm-msg">
              Are you sure you want to delete <span className="dr-confirm-filename">"{deleteConfirm.fileName}"</span>?
            </p>
            <div className="dr-confirm-actions">
              <button type="button" className="dr-confirm-btn-cancel" onClick={closeDeleteConfirm}>Cancel</button>
              <button type="button" className="dr-confirm-btn-delete" onClick={() => deleteConfirm.onConfirm?.()}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate MVR Filename Confirmation Modal */}
      {mvrDuplicateQueue.length > 0 && (
        <div className="dam-overlay" onClick={cancelMvrDuplicateUpload}>
          <div className="dr-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="dam-header">
              <span className="dam-title">Duplicate File Name</span>
              <button className="dam-close-btn" onClick={cancelMvrDuplicateUpload}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p className="dr-confirm-msg">
              A file named <span className="dr-confirm-filename">"{mvrDuplicateQueue[0].file.name}"</span> has already been uploaded for this submission. Uploading it again will override the existing data for this file. Do you want to continue?
            </p>
            <div className="dr-confirm-actions">
              <button type="button" className="dr-confirm-btn-cancel" onClick={cancelMvrDuplicateUpload}>No</button>
              <button type="button" className="dr-confirm-btn-confirm" onClick={confirmMvrDuplicateUpload}>Yes, Override</button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Summary Modal */}
      {summaryModalOpen && <SubmissionSummaryModal data={submissionSummary} loading={submissionSummaryLoading} verdict={rulesVerdict} verdictLoading={rulesVerdictLoading} verdictError={rulesVerdictError} relatedCarriers={relatedCarriers} relatedFlagged={relatedFlagged} onClose={() => setSummaryModalOpen(false)} />}

      {/* MVR View Modal */}
      {mvrViewModal && <DocAnalysisModal title={mvrViewModal.title} data={mvrViewModal.data} onClose={() => setMvrViewModal(null)} />}

      {fetchMvrOpen && (
        <FetchMvrModal
          quoteId={analysisQuoteIdRef.current || quoteId || ""}
          drivers={siDrivers}
          onClose={() => setFetchMvrOpen(false)}
          onImported={(driverName: string) => {
            toast.current?.show({
              severity: "success", summary: "MVR added",
              detail: `${driverName}'s MVR has been added to this quote.`, life: 4000,
            });
            refreshMvrFiles();
            callSubmissionStatus(analysisQuoteIdRef.current || quoteId);
          }}
        />
      )}

      {orderMvrOpen && (
        <OrderMvrModal
          quoteId={analysisQuoteIdRef.current || quoteId || ""}
          drivers={siDrivers}
          insuredName={ii?.name}
          onClose={() => setOrderMvrOpen(false)}
          onOrderComplete={(orderId) => {
            // Report is ready - refresh status so the new MVR is picked up.
            toast.current?.show({
              severity: "success", summary: "MVR ready",
              detail: `Order ${orderId} is ready. Use "Import existing MVR" to add it to this quote.`, life: 6000,
            });
            callSubmissionStatus(analysisQuoteIdRef.current || quoteId);
          }}
        />
      )}

      {/* Doc Analysis Modals */}
      {cabView.open && cabView.result && (
        <DocAnalysisModal title="CAB Report Analysis" data={cabView.result} relatedCarriers={relatedCarriers} relatedFlagged={relatedFlagged} onClose={() => setCabView((v) => ({ ...v, open: false }))} />
      )}
      {lossRunViewModal && (
        <DocAnalysisModal title={lossRunViewModal.title} data={lossRunViewModal.data} onClose={() => setLossRunViewModal(null)} />
      )}
      {safersView.open && safersView.result && (
        <DocAnalysisModal title="SAFERS Analysis" data={safersView.result} safersAlerts={safersAlerts} onClose={() => setSafersView((v) => ({ ...v, open: false }))} />
      )}


      {/* Data Room Header */}
      <div className="dr-header">
        <div className="dr-header__text">
          <div className="dr-header__company-row">
            <h2>{val(ii.name)}</h2>
            <span className={statusClass(status)}>{status ?? "Pending"}</span>
          </div>
          <div className="dr-header__meta">
            <span>{quoteId ?? "QT---"}</span>
            <span className="dr-header__dot" />
            <span>DOT #{val(ii.dotNumber)}</span>
            {((si?.vehicles ?? []).filter(Boolean).length > 0 || siDrivers.length > 0) && (
              <>
                <span className="dr-header__dot" />
                {(si?.vehicles ?? []).filter(Boolean).length > 0 && (
                  <span className="dr-header__chip">{(si?.vehicles ?? []).filter(Boolean).length} vehicles</span>
                )}
                {(si?.vehicles ?? []).filter(Boolean).length > 0 && siDrivers.length > 0 && (
                  <span className="dr-header__chip-sep">·</span>
                )}
                {siDrivers.length > 0 && (
                  <span className="dr-header__chip">{siDrivers.length} drivers</span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="dr-header__actions">
          <button
            className="dr-notes-btn"
            onClick={() => setNotesDrawerOpen(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Notes
            {notes.length > 0 && <span className="dr-notes-btn__badge">{notes.length}</span>}
          </button>
          <button
            className="dr-ai-btn"
            disabled={submissionSummaryLoading}
            onClick={async () => {
              setSummaryModalOpen(true);
              callRulesVerdict();
              await callSubmissionSummary();
            }}
          >
            {submissionSummaryLoading ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="dr-spin">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            )}
            Submission AI Summary
          </button>

        </div>
      </div>
      <div className="dr-body">
        <div className="dr-cards-grid">

          {/* ── MVR Card ── */}
          {siDrivers.length > 0 && (() => {
            const allMvrFiles = driverMvrs.flatMap((dm, driverIdx) =>
              dm.files.map((mf, fileIdx) => ({ mf, driverIdx, fileIdx }))
            );
            const hasAnyFiles = allMvrFiles.length > 0;
            return (
              <div className={`dr-doc-card dr-doc-card--${mvrDocStatus}`}>
                <div className="dr-doc-card__head">
                  <div className="dr-doc-card__icon-wrap">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 15s.5-4 5-4 5 4 5 4" /><circle cx="12" cy="9" r="2" />
                    </svg>
                  </div>
                  <div className="dr-doc-card__meta">
                    <div className="dr-doc-card__title-row">
                      <span className="dr-doc-card__title">MVR Files</span>
                      <span className="dr-doc-card__multi-tag">Multi-file</span>
                    </div>
                    <span className="dr-doc-card__subtitle">AI extracts driver names automatically</span>
                  </div>
                  {submissionSummaryLoading
                    ? <span className="dr-doc-card__pill dr-doc-card__pill--sk sk-shimmer" />
                    : (() => {
                      const p = getDocPillStatus("MVR", mvrDocStatus === "checked", mvrDocStatus);
                      const apiVal = submissionSummary?.documents?.["MVR"];
                      const label = apiVal ? p.label : (p.cls === "checked" ? "Processed" : p.cls === "uploaded" ? "Processing" : "Pending");
                      return <span className={`dr-doc-card__pill dr-doc-card__pill--${p.cls}`}>{label}</span>;
                    })()
                  }
                </div>

                {hasAnyFiles && (
                  <div className="dr-doc-card__files">
                    {allMvrFiles.map(({ mf, driverIdx, fileIdx }) => {
                      const mvrAnalyzed = !!mf.result || !!mf.restoredName;
                      const mvrReady = mf.result?.data?.ready ?? (mf.result as any)?.ready ?? true;
                      const mvrUnreadable = mvrAnalyzed && !mvrReady;
                      const fileState = mvrUnreadable || mf.error ? "err" : mvrAnalyzed ? "ok" : mf.loading ? "loading" : "pending";
                      return (
                        <div key={`${driverIdx}-${fileIdx}`} className={`dr-card-file dr-card-file--${fileState}`}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                          </svg>
                          <div className="dr-card-file__info">
                            <span className="dr-card-file__name">{mf.file?.name ?? mf.restoredName}</span>
                            <span className="dr-card-file__size">{mf.file ? formatBytes(mf.file.size) : formatBytes(mf.restoredSize ?? 0)}</span>
                          </div>
                          <div className="dr-card-file__actions">
                            {mf.loading && <span className="dr-card-file__spin">{spinnerSvg}Analyzing…</span>}
                            {!mvrAnalyzed && !mf.loading && mf.file && (
                              <button type="button" onClick={() => uploadMVRToAPI(driverIdx, fileIdx)} className="dr-card-file__retry">Retry</button>
                            )}
                            {mvrAnalyzed && (
                              <>
                                <button type="button" onClick={() => viewMVRAnalysis(driverIdx, fileIdx)} className={`dr-card-file__view${mf.viewLoading ? " dr-card-file__view--loading" : ""}`}>
                                  {mf.viewLoading ? <>{spinnerSvg}Loading…</> : <>{eyeSvg}View</>}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteMVRFile(driverIdx, fileIdx)}
                                  disabled={deletingMvr.has(`${driverIdx}-${fileIdx}`)}
                                  className={`dr-card-file__delete${deletingMvr.has(`${driverIdx}-${fileIdx}`) ? " dr-card-file__delete--loading" : ""}`}
                                  title="Delete document"
                                >
                                  {deletingMvr.has(`${driverIdx}-${fileIdx}`) ? spinnerSvg : trashSvg}
                                </button>
                              </>
                            )}
                          </div>
                          {mf.error && <div className="dr-card-file__error">{mf.error}</div>}
                          {mvrUnreadable && !mf.error && (
                            <div className="dr-card-file__error">No driver details could be read from this file. Please check it is a valid MVR report and upload a clearer copy.</div>
                          )}
                        </div>
                      );
                    })}
                    <div className="dr-doc-card__file-count">{mvrAnalyzedCount} of {mvrTotalCount} analyzed</div>
                  </div>
                )}

                {/* Retrieval half of the order flow - an ordered MVR is filed under the
                    agent's WorkEmail and imported from here. Also lists MVRs ordered
                    directly in the Toolbox. */}
                <button
                  type="button"
                  className="dr-mvr-fetch-btn"
                  onClick={() => setFetchMvrOpen(true)}
                  disabled={siDrivers.length === 0}
                  title={siDrivers.length === 0 ? "No drivers on this quote" : "Import an MVR that has already been ordered"}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Import existing MVR
                </button>

                <button
                  type="button"
                  className="dr-mvr-fetch-btn"
                  onClick={() => setOrderMvrOpen(true)}
                  disabled={siDrivers.length === 0}
                  title={siDrivers.length === 0 ? "No drivers on this quote" : "Order a new MVR"}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  Order new MVR
                </button>

                <div
                  className={`dr-doc-card__drop${driverMvrs[0]?.dragOver ? " dr-doc-card__drop--active" : ""}`}
                  onDragOver={(e) => handleMvrDragOver(0, e)}
                  onDragLeave={handleMvrDragLeave}
                  onDrop={(e) => handleMvrDrop(0, e)}
                >
                  <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => handleMvrFileChange(0, e)} />
                  {uploadSvg}
                  <span>{hasAnyFiles ? "Add more files" : "Drop files or click to upload"}</span>
                </div>
              </div>
            );
          })()}

          {/* ── Loss Run Card ── */}
          {(() => {
            const lrAnalyzedCount = lossRunFiles.filter((f) => f.result || f.restoredName).length;
            const lrTotalCount = lossRunFiles.length;
            const lrDocStatus: DocStatus = lrTotalCount === 0 ? "missing" : lrAnalyzedCount > 0 ? "checked" : "uploaded";
            const lrPill = submissionSummaryLoading ? { label: "", cls: "sk" } : getDocPillStatus("LOSS_RUN", lrDocStatus === "checked", lrDocStatus);
            return (
              <div className={`dr-doc-card dr-doc-card--${lrDocStatus}`}>
                <div className="dr-doc-card__head">
                  <div className="dr-doc-card__icon-wrap">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                      <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="15" y2="17" />
                    </svg>
                  </div>
                  <div className="dr-doc-card__meta">
                    <div className="dr-doc-card__title-row">
                      <span className="dr-doc-card__title">Loss Run</span>
                      <span className="dr-doc-card__multi-tag">Multi-file</span>
                    </div>
                    <span className="dr-doc-card__subtitle">Up to 3 years of claims history (issued within 30 days)</span>
                  </div>
                  <span className={`dr-doc-card__pill dr-doc-card__pill--${lrPill.cls}${lrPill.cls === "sk" ? " sk-shimmer" : ""}`}>{lrPill.label}</span>
                </div>

                {lossRunFiles.length > 0 && (
                  <div className="dr-doc-card__files">
                    {lossRunFiles.map((lf, fileIdx) => {
                      const lrFileAnalyzed = !!lf.result || !!lf.restoredName;
                      const lrReady = lf.result?.data?.ready ?? (lf.result as any)?.ready ?? true;
                      const lrUnreadable = lrFileAnalyzed && !lrReady;
                      const fileState = lrUnreadable || lf.error ? "err" : lrFileAnalyzed ? "ok" : lf.loading ? "loading" : "pending";
                      return (
                        <div key={fileIdx} className={`dr-card-file dr-card-file--${fileState}`}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                          </svg>
                          <div className="dr-card-file__info">
                            <span className="dr-card-file__name">{lf.file?.name ?? lf.restoredName}</span>
                            <span className="dr-card-file__size">{lf.file ? formatBytes(lf.file.size) : formatBytes(lf.restoredSize ?? 0)}</span>
                          </div>
                          <div className="dr-card-file__actions">
                            {lf.loading && <span className="dr-card-file__spin">{spinnerSvg}Analyzing…</span>}
                            {!lrFileAnalyzed && !lf.loading && lf.file && (
                              <button type="button" onClick={() => uploadLossRunToAPI(fileIdx)} className="dr-card-file__retry">Retry</button>
                            )}
                            {lrFileAnalyzed && (
                              <>
                                <button type="button" onClick={() => viewLossRunAnalysis(fileIdx)} className={`dr-card-file__view${lf.viewLoading ? " dr-card-file__view--loading" : ""}`}>
                                  {lf.viewLoading ? <>{spinnerSvg}Loading…</> : <>{eyeSvg}View</>}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteLossRunFile(fileIdx)}
                                  disabled={deletingLossRun.has(fileIdx)}
                                  className={`dr-card-file__delete${deletingLossRun.has(fileIdx) ? " dr-card-file__delete--loading" : ""}`}
                                  title="Delete document"
                                >
                                  {deletingLossRun.has(fileIdx) ? spinnerSvg : trashSvg}
                                </button>
                              </>
                            )}
                          </div>
                          {lf.error && <div className="dr-card-file__error">{lf.error}</div>}
                          {lrUnreadable && !lf.error && (
                            <div className="dr-card-file__error">No claims details could be read from this file. Please check it is a valid loss run report and upload a clearer copy.</div>
                          )}
                        </div>
                      );
                    })}
                    <div className="dr-doc-card__file-count">{lrAnalyzedCount} of {lrTotalCount} analyzed</div>
                  </div>
                )}

                <div
                  className={`dr-doc-card__drop${lossRunDragOver ? " dr-doc-card__drop--active" : ""}`}
                  onDragOver={handleLossRunDragOver}
                  onDragLeave={handleLossRunDragLeave}
                  onDrop={handleLossRunDrop}
                >
                  <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={handleLossRunFileChange} />
                  {uploadSvg}
                  <span>{lossRunFiles.length > 0 ? "Add more files" : "Drop files or click to upload"}</span>
                </div>
              </div>
            );
          })()}

          {/* ── SAFERS Card ── */}
          {(() => {
            const doc = docs.find((d) => d.id === "safers")!;
            const isAnalyzed = !!safersResult || !!safersView.result || doc.status !== "missing";
            return (
              <div className={`dr-doc-card dr-doc-card--${doc.status}`}>
                <div className="dr-doc-card__head">
                  <div className="dr-doc-card__icon-wrap">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <div className="dr-doc-card__meta">
                    <span className="dr-doc-card__title">SAFERS</span>
                    <span className="dr-doc-card__subtitle">Motor carrier safety rating from FMCSA SAFER system</span>
                  </div>
                  {submissionSummaryLoading
                    ? <span className="dr-doc-card__pill dr-doc-card__pill--sk sk-shimmer" />
                    : (() => {
                      const p = getDocPillStatus("SAFERS", isAnalyzed, doc.status);
                      const apiVal = submissionSummary?.documents?.["SAFERS"];
                      const label = apiVal ? p.label : (isAnalyzed ? "Processed" : safersLoading ? "Loading…" : "Pending");
                      return <span className={`dr-doc-card__pill dr-doc-card__pill--${p.cls}`}>{label}</span>;
                    })()
                  }
                </div>

                {/* SAFER underwriting alerts — shown as soon as SAFER resolves. */}
                {safersAlerts.length > 0 && (
                  <button
                    type="button"
                    className={`dr-safers-alert${safersAlerts.some((a) => a.severity === "high") ? " dr-safers-alert--high" : ""}`}
                    onClick={viewSafersAnalysis}
                    title="Open the SAFERS analysis for detail"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span className="dr-safers-alert__text">
                      {safersAlerts.map((a) => a.label).join(" · ")} need{safersAlerts.length === 1 ? "s" : ""} review
                    </span>
                    <span className="dr-safers-alert__cta">Review</span>
                  </button>
                )}

                {isAnalyzed && (
                  <div className="dr-doc-card__files">
                    <div className="dr-card-file dr-card-file--ok">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                      <div className="dr-card-file__info">
                        <span className="dr-card-file__name">SAFERS Data</span>
                        <span className="dr-card-file__size">DOT #{editableDotNumber}</span>
                      </div>
                      <div className="dr-card-file__actions">
                        <button type="button" onClick={viewSafersAnalysis} className={`dr-card-file__view${safersView.loading ? " dr-card-file__view--loading" : ""}`}>
                          {safersView.loading ? <>{spinnerSvg}Loading…</> : <>{eyeSvg}View</>}
                        </button>
                        <button
                          type="button"
                          onClick={deleteSAFERSFile}
                          disabled={deletingSafers}
                          className={`dr-card-file__delete${deletingSafers ? " dr-card-file__delete--loading" : ""}`}
                          title="Delete SAFERS record"
                        >
                          {deletingSafers ? spinnerSvg : trashSvg}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="dr-doc-card__safers">
                  {isAnalyzed && <div className="dr-doc-card__safers-hint">Re-fetch with a different DOT:</div>}
                  <div className="dr-doc-card__safers-row">
                    <input
                      type="text"
                      value={editableDotNumber}
                      onChange={(e) => setEditableDotNumber(e.target.value)}
                      placeholder="Enter DOT number"
                      className="dr-safers-input"
                    />
                    <button
                      type="button"
                      disabled={safersLoading}
                      onClick={uploadSAFERSToAPI}
                      className={`dr-safers-fetch-btn dr-safers-fetch-btn--${safersLoading ? "loading" : "ready"}`}
                    >
                      {safersLoading ? <>{spinnerSvg}Fetching…</> : <>Fetch SAFERS</>}
                    </button>
                  </div>
                  {safersError && <div className="dr-card-file__error">{safersError}</div>}
                </div>
              </div>
            );
          })()}

          {/* ── CAB Card ── */}
          {(() => {
            const doc = docs.find((d) => d.id === "cab")!;
            const isAnalyzed = !!cabResult || !!doc.restoredName;
            return (
              <div className={`dr-doc-card dr-doc-card--${doc.status}`}>
                <div className="dr-doc-card__head">
                  <div className="dr-doc-card__icon-wrap">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </div>
                  <div className="dr-doc-card__meta">
                    <span className="dr-doc-card__title">CAB Report</span>
                    <span className="dr-doc-card__subtitle">Carrier Assessment and Benchmarking report</span>
                  </div>
                  {submissionSummaryLoading
                    ? <span className="dr-doc-card__pill dr-doc-card__pill--sk sk-shimmer" />
                    : (() => { const p = getDocPillStatus("CAB", isAnalyzed, doc.status); return <span className={`dr-doc-card__pill dr-doc-card__pill--${p.cls}`}>{p.label}</span>; })()
                  }
                </div>

                {/* Shown as soon as CAB resolves, not only on opening the summary. */}
                {relatedFlagged && (
                  <button
                    type="button"
                    className="dr-related-banner"
                    onClick={() => setSummaryModalOpen(true)}
                    title="Open the Submission Summary to see the related carriers"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span className="dr-related-banner__text">
                      {relatedCarriers.length > 0
                        ? <>Linked to <strong>{relatedCarriers.length}</strong> other {relatedCarriers.length === 1 ? "carrier" : "carriers"}
                            {relatedCarriers[0]?.legalName ? <> — {relatedCarriers[0].legalName}{relatedCarriers.length > 1 ? " and others" : ""}</> : null}</>
                        : <>Related carrier flagged for this DOT</>}
                    </span>
                    <span className="dr-related-banner__cta">Review</span>
                  </button>
                )}

                {/* DOT-fetched CAB data row */}
                {cabResult && !doc.file && !doc.restoredName && (
                  <div className="dr-doc-card__files">
                    <div className="dr-card-file dr-card-file--ok">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                      <div className="dr-card-file__info">
                        <span className="dr-card-file__name">CAB Data</span>
                        <span className="dr-card-file__size">DOT #{editableDotNumber}</span>
                      </div>
                      <div className="dr-card-file__actions">
                        <button type="button" onClick={viewCabAnalysis} className={`dr-card-file__view${cabView.loading ? " dr-card-file__view--loading" : ""}`}>
                          {cabView.loading ? <>{spinnerSvg}Loading…</> : <>{eyeSvg}View</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteCABFile(`DOT #${editableDotNumber}`)}
                          disabled={deletingCab}
                          className={`dr-card-file__delete${deletingCab ? " dr-card-file__delete--loading" : ""}`}
                          title="Delete CAB record"
                        >
                          {deletingCab ? spinnerSvg : trashSvg}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* File-uploaded CAB row */}
                {(doc.file || doc.restoredName) && (
                  <div className="dr-doc-card__files">
                    <div className={`dr-card-file dr-card-file--${isAnalyzed ? "ok" : cabError ? "err" : cabLoading ? "loading" : "pending"}`}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                      </svg>
                      <div className="dr-card-file__info">
                        <span className="dr-card-file__name">{doc.file?.name ?? doc.restoredName}</span>
                        <span className="dr-card-file__size">{doc.file ? formatBytes(doc.file.size) : formatBytes(doc.restoredSize ?? 0)}</span>
                      </div>
                      <div className="dr-card-file__actions">
                        {cabLoading && <span className="dr-card-file__spin">{spinnerSvg}Analyzing…</span>}
                        {!isAnalyzed && !cabLoading && doc.file && (
                          <button type="button" onClick={() => uploadCABToAPI(doc.file!)} className="dr-card-file__retry">Retry</button>
                        )}
                        {isAnalyzed && (
                          <>
                            <button type="button" onClick={viewCabAnalysis} className={`dr-card-file__view${cabView.loading ? " dr-card-file__view--loading" : ""}`}>
                              {cabView.loading ? <>{spinnerSvg}Loading…</> : <>{eyeSvg}View</>}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteCABFile(doc.file?.name ?? doc.restoredName ?? "CAB Report")}
                              disabled={deletingCab}
                              className={`dr-card-file__delete${deletingCab ? " dr-card-file__delete--loading" : ""}`}
                              title="Delete CAB report"
                            >
                              {deletingCab ? spinnerSvg : trashSvg}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {(doc.file || doc.restoredName) ? (
                  <div className="dr-doc-card__replace-row">
                    <label className="dr-card-replace-link">
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => handleFileChange(doc.id, e)} />
                      {uploadSvg} Replace file
                    </label>
                  </div>
                ) : !cabResult && (
                  <div
                    className={`dr-doc-card__drop${doc.dragOver ? " dr-doc-card__drop--active" : ""}`}
                    onDragOver={(e) => handleDragOver(doc.id, e)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(doc.id, e)}
                  >
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => handleFileChange(doc.id, e)} />
                    {uploadSvg}
                    <span>Drop file or click to upload</span>
                  </div>
                )}

                <div className="dr-doc-card__safers">
                  {isAnalyzed && <div className="dr-doc-card__safers-hint">Re-fetch with a different DOT:</div>}
                  <div className="dr-doc-card__safers-row">
                    <input
                      type="text"
                      value={editableDotNumber}
                      onChange={(e) => setEditableDotNumber(e.target.value)}
                      placeholder="Enter DOT number"
                      className="dr-safers-input"
                    />
                    <button
                      type="button"
                      disabled={cabLoading}
                      onClick={fetchCABByDot}
                      className={`dr-safers-fetch-btn dr-safers-fetch-btn--${cabLoading ? "loading" : "ready"}`}
                    >
                      {cabLoading ? <>{spinnerSvg}Fetching…</> : <>Fetch CAB</>}
                    </button>
                  </div>
                  {cabError && <div className="dr-card-file__error">{cabError}</div>}
                </div>
              </div>
            );
          })()}

        </div>

        {/* Submission Readiness Summary */}
        <div className="dr-summary">
          {submissionSummaryLoading && (
            <div className="dr-loading-overlay">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="dr-spin--fast">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className="dr-loading-overlay__text">Generating AI Summary…</span>
            </div>
          )}
          <div className="dr-summary__header">
            <p className="dr-summary__title">Submission Readiness Summary</p>
            <div className="dr-summary__header-actions">
              {submissionSummaryLoading && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a7a3a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="dr-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              )}
            </div>
          </div>
          <div className="dr-summary__body">
            {/* Quality score */}
            <div className="dr-quality">
              <span className="dr-quality__label">Quality of Application</span>
              <div className="dr-quality__track">
                <div
                  className={`dr-quality__fill${submissionSummaryLoading ? " sk-shimmer" : ""} dr-quality__fill--${submissionSummary && !submissionSummaryLoading ? ((submissionSummary.qualityScore ?? 0) >= 75 ? "great" : (submissionSummary.qualityScore ?? 0) >= 50 ? "good" : (submissionSummary.qualityScore ?? 0) >= 25 ? "fair" : "poor") : tier.cls}`}
                  style={{ width: `${submissionSummaryLoading ? 100 : submissionSummary ? (submissionSummary.qualityScore ?? readinessPct) : readinessPct}%` }}
                />
              </div>
              <div className="dr-quality__meta">
                {submissionSummaryLoading
                  ? <span className="dr-quality__pct" style={{ color: "#888", fontSize: "0.85em" }}>Refreshing…</span>
                  : <span className="dr-quality__pct">{submissionSummary ? (submissionSummary.qualityScore ?? readinessPct) : readinessPct}%</span>
                }
                {submissionSummary && !submissionSummaryLoading && (
                  <span className={`dr-quality-ready-pill dr-quality-ready-pill--${submissionSummary.canSubmit ? "ok" : "fail"}`}>
                    {submissionSummary.canSubmit ? "✓ Ready to Submit" : (submissionSummary.status ?? "").replaceAll("_", " ")}
                  </span>
                )}
              </div>
            </div>

            {/* Document status */}
            <div className="dr-summary-list">
              <div className="dr-summary-list__label">Documents</div>
              {submissionSummary && !submissionSummaryLoading ? (
                Object.entries(submissionSummary.documents).map(([docType, docStatus]) => {
                  const ready = docStatus === "PROCESSED" || docStatus === "READY";
                  const missing = docStatus === "MISSING";
                  const cls = ready ? "ok" : missing ? "missing" : "uploaded";
                  return (
                    <div key={docType} className="dr-summary-item">
                      <div className={`dr-summary-item__icon dr-summary-item__icon--${cls}`}>
                        {ready ? (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        )}
                      </div>
                      <span className="dr-summary-item__label">{docType.replaceAll("_", " ")}</span>
                      <span className={`dr-summary-item__status dr-summary-item__status--${cls}`}>{docStatus}</span>
                    </div>
                  );
                })
              ) : submissionSummaryLoading ? (
                /* Shimmer placeholders while LLM refreshes — avoids showing stale MISSING */
                <>
                  {["MVR", "LOSS_RUN", "SAFERS", "CAB"].map((doc) => (
                    <div key={doc} className="dr-summary-item">
                      <div className="dr-summary-item__icon dr-summary-item__icon--sk sk-shimmer" style={{ width: 18, height: 18, borderRadius: 4 }} />
                      <span className="dr-summary-item__label sk-shimmer" style={{ width: 64, height: 12, borderRadius: 4, display: "inline-block" }} />
                      <span className="dr-summary-item__status sk-shimmer" style={{ width: 48, height: 12, borderRadius: 4, display: "inline-block" }} />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {siDrivers.length > 0 && (
                    <div className="dr-summary-item">
                      <div
                        className={`dr-summary-item__icon dr-summary-item__icon--${mvrDocStatus === "checked" ? "ok" : mvrDocStatus === "uploaded" ? "uploaded" : "missing"}`}
                      >
                        {mvrDocStatus !== "missing" ? (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        )}
                      </div>
                      <span className="dr-summary-item__label">MVR Status</span>
                      <span
                        className={`dr-summary-item__status dr-summary-item__status--${mvrDocStatus === "checked" ? "ok" : mvrDocStatus === "uploaded" ? "uploaded" : "missing"}`}
                      >
                        {mvrAnalyzedCount}/{mvrTotalCount} Files
                      </span>
                    </div>
                  )}
                  {docs.map((doc) => {
                    const cls = doc.status === "checked" ? "ok" : doc.status === "uploaded" ? "uploaded" : "missing";
                    return (
                      <div key={doc.id} className="dr-summary-item">
                        <div className={`dr-summary-item__icon dr-summary-item__icon--${cls}`}>
                          {doc.status !== "missing" ? (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                        </div>
                        <span className="dr-summary-item__label">{doc.label}</span>
                        <span className={`dr-summary-item__status dr-summary-item__status--${cls}`}>
                          {doc.status === "checked" ? "Analyzed" : doc.status === "uploaded" ? "Uploaded" : "Missing"}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* AI Summary */}
            {/* {submissionSummary?.summary && (
              <div className="dr-ai-summary-box">
                <div className="dr-ai-summary-label">AI Summary</div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {submissionSummary.summary}
                </ReactMarkdown>
              </div>
            )} */}

            {/* This rail is always on screen, unlike the summary modal. */}
            {relatedFlagged && (
              <button
                type="button"
                className={`dr-summary-related${relatedCarriers.some(isStrongMatch) ? " dr-summary-related--high" : ""}`}
                onClick={() => setSummaryModalOpen(true)}
              >
                <div className="dr-summary-related__head">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  Related Carriers
                  <span className="dr-summary-related__count">{relatedCarriers.length || "!"}</span>
                </div>
                {relatedCarriers.slice(0, 3).map((c) => (
                  <div key={c.dotNumber} className="dr-summary-related__row">
                    <span className="dr-summary-related__name">{c.legalName || `DOT ${c.dotNumber}`}</span>
                    <span className="dr-summary-related__match">{(c.matchedOn ?? []).join(" · ")}</span>
                  </div>
                ))}
                {relatedCarriers.length > 3 && (
                  <div className="dr-summary-related__row">
                    <span className="dr-summary-related__name">+{relatedCarriers.length - 3} more</span>
                  </div>
                )}
                <span className="dr-summary-related__cta">Review before submitting →</span>
              </button>
            )}

            {/* Driver MVR status from AI */}
            {submissionSummary?.driverMvrStatus && submissionSummary.driverMvrStatus.length > 0 && (
              <div className="dr-mvr-status-section">
                <div className="dr-mvr-status-label">Driver MVR Status</div>
                <div className="dr-mvr-status-list">
                  {submissionSummary.driverMvrStatus.map((d: any, i: number) => (
                    <div key={i} className="dr-mvr-status-item">
                      <span className="dr-mvr-status-name">{d.driverName || d.name || `Driver ${i + 1}`}</span>
                      <span className={`dr-mvr-status-value--${d.mvrStatus === "PRESENT" ? "present" : "missing"}`}>
                        {d.mvrStatus}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>


      </div>

      {/* Footer */}
      <div className="dr-footer">
        <button className="dr-back-btn" onClick={onBack}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <button
          className="dr-upload-btn"
          disabled
        >
          {submitting ? (
            <>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="dr-spin"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Uploading…
            </>
          ) : (
            <>
              Upload to MTM
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </>
          )}
        </button>
      </div>

      {ReactDOM.createPortal(
        <AnimatePresence>
          {notesDrawerOpen && (
            <>
              <motion.div
                className="qd-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setNotesDrawerOpen(false)}
              />
              <motion.div
                className="qd-panel"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
              >
                {/* Header */}
                <div className="notes-drawer__header">
                  <div className="notes-drawer__title-row">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span>AMS Notes</span>
                    {notesLoading && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="dr-spin">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    )}
                    {notes.length > 0 && <span className="notes-drawer__count">{notes.length}</span>}
                  </div>
                  <button className="qd-close-btn" onClick={() => setNotesDrawerOpen(false)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                {/* Compose */}
                <div className="notes-drawer__compose">
                  <textarea
                    className="notes-drawer__textarea"
                    placeholder="Add a note… (Ctrl+Enter to save)"
                    value={newNoteText}
                    rows={3}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submitNote(); }}
                  />
                  <button
                    type="button"
                    className="notes-drawer__submit"
                    disabled={noteSubmitting || !newNoteText.trim()}
                    onClick={submitNote}
                  >
                    {noteSubmitting ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="dr-spin">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    )}
                    Save Note
                  </button>
                </div>

                {/* List */}
                <div className="notes-drawer__list">
                  {notes.length === 0 && !notesLoading && (
                    <div className="notes-drawer__empty">No notes yet for this insured.</div>
                  )}
                  {notes.map((n) => (
                    <div key={n.databaseId} className="notes-drawer__item">
                      <div className="notes-drawer__item-body">{stripHtml(n.subject) || "—"}</div>
                      <div className="notes-drawer__item-meta">
                        {n.noteType && <><span className="nd-type">{n.noteType}</span><span className="nd-dot" /></>}
                        <span>{n.createdBy ?? "—"}</span>
                        <span className="nd-dot" />
                        <span>{n.createDate ? new Date(n.createDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                        {n.policy?.number && <><span className="nd-dot" /><span className="nd-policy">{n.policy.carrierName} · {n.policy.number}</span></>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default DataRoomStep;
